import { blinkConstants } from '@thoughtspot/blink-constants';
import { CustomizableChartFeature, CustomStylingConfig, } from '@thoughtspot/custom-style-service';
import * as dateUtil from '@thoughtspot/date-format-util';
import { getFormattedValueForNumericColumn } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import { mapArrayToBooleanHash } from '@thoughtspot/js-util';
import { formatDataValue } from '@thoughtspot/number-format-util';
import { enableAttributesInPivotDataArea, enableMuzeStudio, isAdvancedPivotTableEnabled, isChartAggregatedValOnKPIEnabled, isContextualChangeAnalysisEnabled, isCustomChartEnabled, isCustomComparisonOnChartEnabled, isFetchPivotSummaryFromBackendEnabled, isGenericCustomCompareEnabled, isKPIAnomaliesEnabled, isKPIChartOnAttributesEnabled, isPieChartEnhancementsEnabled, isSpotIQV2Enabled, isStandardRegionEnabled, muzeBubbleChartEnabled, muzeChartPhase1Enabled, muzeDonutChartEnabled, muzeHeatmapChartEnabled, muzeParetoChartEnabled, muzeScatterChartEnabled, muzeWaterfallChartEnabled, } from '@thoughtspot/session-service';
import { assign } from '@thoughtspot/string-util';
import { getColumnSortPriorityOrder, getId, getTimeBucket, getTrueEffectiveAggregateType, isAttribute, isDateColumn, isDateFamilyColumn, isEffectivelyNumeric, isGeoColumn, isGrowth, isMeasure, isTimeColumn, } from '@thoughtspot/visualization-column-util';
import chroma from 'chroma-js';
import _ from 'lodash';
import { ABOVE_EXPECTED_COLOR_INDEX, ANOMALY_BAR_HEIGHT, anomalyColorIndex, BELOW_EXPECTED_COLOR_INDEX, boundColorIndex, getAnomalyColor, isPointAnomaly, isShowAnomaliesEnabled, isShowAnomaliesSupported, isShowBoundsEnabled, } from '../components/kpi/cortex-utils';
import { getCustomComparisonOptions, getEffectiveRefTuple, getKPISeriesXAxisValidationError, getRefTuple, isAnalyseChangeCtaEnabled, isComparisionEnabled, isKpiChartTypeTimeseries, isSinglePointKPIEnabledWithValue, isSparklineEnabled, kpiChartEvents, mapTimeBucketToCategory, negativeComparisionColorIndex, positiveComparisionColorIndex, validXAxisColumnTimeBuckets, } from '../components/kpi/kpi-utils';
import { containsAllNegativeValuesOnSizeColumn, containsAllZeroValuesOnY, containsNegativeValuesOnY, getDefaultQueryData, getLegendColumnValues, getMinYValue, getSamplingRatio, getSeries, getTotalRowCount, } from '../data/data-util';
import { getMaxExpandedPathLength } from '../devextreme/model/pivot-data-model-utils';
import { getEnvFlags, getTranslatedTextCallback } from '../env';
import { GEO_TILE_TYPES } from '../geomap/base/geo-constants';
import { getDataLabelFromColumn } from '../highcharts/blink-highchart-config-util';
import { ChartThemeService, CONTRAST_SETTINGS, } from '../theme/chart-theme-service';
import { ChartType, ColumnAggregationType, GeometryType, } from '../types';
import { ChartSpecificColumnType, TupleType, } from '../types/prism';
import { createLogger } from '../utils/logger';
import { validateAxisColumns } from './axis-column-validation-service';
import { testXAxisLegendValid } from './cardinality-validation-service';
import { AdvancedSection, } from './chart-allowed-configurations-config';
import { ChartConfigOptions } from './chart-config-options';
import { ErrorTypes } from './chart-data-validation';
import { areAllTimeSeriesColumns, getCategoryColumns, getChartSpecificCustomProps, getChartType, getLegendColumns, getPivotState, getPivotSummariesState, getRadialColumn, getSageColumns, getStackLabelDecimalPrecision, getUseTreeLayout, getXAxisColumns, getYAxisColumn, getYAxisColumns, isAllowedCustomColorPallete, isDescendingSort, isFirstYAxisColumn, isMarkersEnabled, isYAxisColumn, isYAxisStackedAsPercent, } from './chart-model-utils';
import { GeoConfigType } from './chart-ui-utils';
import { getName } from './column-name-util';
import { DEFAULT_ADVANCED_BAR_CHART_MAPPING, DEFAULT_ADVANCED_BUBBLE_CHART_MAPPING, DEFAULT_ADVANCED_CHART_MAPPING, DEFAULT_ADVANCED_DONUT_CHART_MAPPING, DEFAULT_ADVANCED_HEATMAP_CHART_MAPPING, DEFAULT_ADVANCED_LINE_COLUMN_CHART_MAPPING, DEFAULT_ADVANCED_PARETO_CHART_MAPPING, DEFAULT_ADVANCED_SCATTER_CHART_MAPPING, DEFAULT_ADVANCED_WATERFALL_CHART_MAPPING, DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER, DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_HEATMAP_CHART, DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_HORIZONTAL_CHARTS, DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_LINE_COLUMN_CHART, MIN_MEASURES_FOR_LINE_COLUMN_CHART, } from './custom-chart-config-utils';
import { customSortOnSeriesData, getCustomOrderInSequenceWithoutColumnId, getVizColumnsSortOrder, isCustomSortSupportedForChartType, } from './custom-sort-utils';
import { getGeoConfig } from './geo-column-utils';
import { containsMeasureNamesOrMeasureValuesColumns, } from './measure-names-and-values';
import * as pointSelectionServiceConfig from './point-selection-service/config';
import * as pointSelectionUtil from './point-selection-service/util';
import { seriesOverrides } from './series-overrides';
export var TooltipLabelType;
(function (TooltipLabelType) {
    TooltipLabelType["X_LABEL"] = "X_LABEL";
    TooltipLabelType["Y_LABEL"] = "Y_LABEL";
    TooltipLabelType["SERIES_LABEL"] = "SERIES_LABEL";
    TooltipLabelType["CATEGORY_LABEL"] = "CATEGORY_LABEL";
    TooltipLabelType["RADIAL_LABEL"] = "RADIAL_LABEL";
})(TooltipLabelType || (TooltipLabelType = {}));
export const CUSTOM_PROP_CONSTRAINTS = {
    NON_DESCENDING_TIME_COLUMN_ON_X: 'nonDescendingTimeColumnOnX',
    NON_ATTRIBUTE_COLUMN_ON_Y: 'nonAttributeColumnOnY',
    SHOW_SPARKLINE_ENABLED: 'showSparklineEnabled',
    SHOW_ANOMALIES_ENABLED: 'showAnomaliesEnabled',
    SHOW_ANALYSE_CHANGE_CTA_ENABLED: 'showAnalyseChangeCta',
    COMPARISON_ENABLED: 'isComparisonEnabled',
    SINGLE_POINT_KPI_ENABLED: 'isSinglePointKPIEnabled',
};
const DEFAULT_CUSTOM_COLORS = [
    blinkConstants.defaultColorPalette.GREEN[2],
    blinkConstants.defaultColorPalette.RED[2],
    blinkConstants.defaultColorPalette.RED[2],
    blinkConstants.defaultColorPalette.GREY[2],
];
const GRADIENT_CUSTOM_COLORS = [
    blinkConstants.gradientColorsColorPalette.GREEN[0],
    blinkConstants.gradientColorsColorPalette.RED[0],
    blinkConstants.gradientColorsColorPalette.GREEN[0],
    blinkConstants.gradientColorsColorPalette.RED[0],
];
const AREA_CHART_ENABLED_GEOMETRIES = [
    GeometryType.Polygon,
    GeometryType.MultiPolygon,
    GeometryType.Circle,
];
export const chartSpecificConstants = {
    MAX_LABEL_LENGTH_PIE: 30,
    LEGEND_CARDINALITY_THRESHOLD: 40,
    PIECHART_CARDINALITY_THRESHOLD: 50,
    TREEMAP_CARDINALITY_THRESHOLD: 300,
    PIE_SIZE: '70%',
    PIE_INNERSIZE_SINGLECHART: '50%',
    PIE_OUTERSIZE_DOUBLECHART: '30%',
    PIE_INNERSIZE_DOUBLECHART: '65%',
    SANKEY_CHART_CARDINALITY_THRESHOLD: 720,
    SPIDER_HIGH_CARDINALITY_THRESHOLD: 32,
    SPIDER_LOW_CARDINALITY_THRESHOLD: 3,
    SPIDER_LABEL_DISTANCE: 30,
    SPIDER_SLICES: 15,
    SPIDER_X_GRID_COLOR: '#119999',
    SPIDER_Y_GRID_COLOR: '#999999',
    DEFAULT_CANDLESTICK_COLORS: _.cloneDeep(DEFAULT_CUSTOM_COLORS),
    DEFAULT_KPI_COLORS: _.cloneDeep(DEFAULT_CUSTOM_COLORS),
    DEFAULT_NEW_KPI_COLORS: _.cloneDeep(GRADIENT_CUSTOM_COLORS),
};
const EMBED_HIDDEN_ACTION_FLAGS = {
    KPIAnalysisCTA: 'kpiAnalysisCTA',
    EnableContextualChangeAnalysis: 'enableContextualChangeAnalysis',
};
export const getCustomColorsSelectorArray = (chartModel) => {
    const defaultCustomColors = getEnvFlags().isKPIWithAnomalyV2Enabled
        ? chartSpecificConstants.DEFAULT_NEW_KPI_COLORS
        : chartSpecificConstants.DEFAULT_KPI_COLORS;
    const customColors = _.get(chartModel, 'vizProp.customColorSelectorArray', []);
    return _.map(defaultCustomColors, (defaultColor, index) => {
        return customColors[index] || defaultColor;
    });
};
const logger = createLogger('chart-type-specification-service');
const CANDLE_STICK_CHART_DATA_KEYS = ['open', 'high', 'low', 'close'];
const chartProviders = {
    HIGHCHART: 'highchart',
    GEO_MAP: 'geomap',
    GEO_EARTH: 'geoearth',
    PIVOT_TABLE: 'pivot_table',
    NETWORK: 'network',
    GRID_TABLE: 'grid_table',
    REACT_COMPONENT: 'react_component',
    INSIGHT: 'insight',
};
const HIDDEN_CHARTS = new Set([
    ChartType.GridTable,
    ChartType.None,
    ChartType.WhiskerScatter,
    ChartType.GeoEarthArea,
    ChartType.GeoEarthBar,
    ChartType.GeoEarthBubble,
    ChartType.GeoEarthGraph,
    ChartType.GeoEarthHeatmap,
]);
const chartTypeByProvider = {
    [ChartType.Bar]: chartProviders.HIGHCHART,
    [ChartType.Column]: chartProviders.HIGHCHART,
    [ChartType.StackedColumn]: chartProviders.HIGHCHART,
    [ChartType.StackedBar]: chartProviders.HIGHCHART,
    [ChartType.Pareto]: chartProviders.HIGHCHART,
    [ChartType.Line]: chartProviders.HIGHCHART,
    [ChartType.Area]: chartProviders.HIGHCHART,
    [ChartType.Scatter]: chartProviders.HIGHCHART,
    [ChartType.Bubble]: chartProviders.HIGHCHART,
    [ChartType.Pie]: chartProviders.HIGHCHART,
    [ChartType.GeoArea]: chartProviders.GEO_MAP,
    [ChartType.GeoBubble]: chartProviders.GEO_MAP,
    [ChartType.GeoHeatmap]: chartProviders.GEO_MAP,
    [ChartType.GeoEarthArea]: chartProviders.GEO_EARTH,
    [ChartType.GeoEarthBubble]: chartProviders.GEO_EARTH,
    [ChartType.GeoEarthBar]: chartProviders.GEO_EARTH,
    [ChartType.GeoEarthHeatmap]: chartProviders.GEO_EARTH,
    [ChartType.GeoEarthGraph]: chartProviders.GEO_EARTH,
    [ChartType.Waterfall]: chartProviders.HIGHCHART,
    [ChartType.Treemap]: chartProviders.HIGHCHART,
    [ChartType.Heatmap]: chartProviders.HIGHCHART,
    [ChartType.StackedArea]: chartProviders.HIGHCHART,
    [ChartType.LineColumn]: chartProviders.HIGHCHART,
    [ChartType.Funnel]: chartProviders.HIGHCHART,
    [ChartType.PivotTable]: chartProviders.PIVOT_TABLE,
    [ChartType.GridTable]: chartProviders.GRID_TABLE,
    [ChartType.LineStackedColumn]: chartProviders.HIGHCHART,
    [ChartType.SpiderWeb]: chartProviders.HIGHCHART,
    [ChartType.Sankey]: chartProviders.NETWORK,
    [ChartType.WhiskerScatter]: chartProviders.HIGHCHART,
    [ChartType.Candlestick]: chartProviders.HIGHCHART,
    [ChartType.None]: chartProviders.GRID_TABLE,
    [ChartType.Kpi]: chartProviders.REACT_COMPONENT,
};
let chartTypeInDisplayOrderWithKpiEnabled = [
    ChartType.Column,
    ChartType.StackedColumn,
    ChartType.Line,
    ChartType.Kpi,
    ChartType.PivotTable,
    ChartType.Pie,
    ChartType.Bar,
    ChartType.StackedBar,
    ChartType.LineColumn,
    ChartType.Area,
    ChartType.StackedArea,
    ChartType.LineStackedColumn,
    ChartType.Scatter,
    ChartType.Bubble,
    ChartType.Waterfall,
    ChartType.Heatmap,
    ChartType.Treemap,
    ChartType.Funnel,
    ChartType.GeoBubble,
    ChartType.GeoHeatmap,
    ChartType.GeoArea,
    ChartType.Sankey,
    ChartType.SpiderWeb,
    ChartType.Candlestick,
    ChartType.Pareto,
];
let chartTypeInDisplayOrder = [
    ChartType.Column,
    ChartType.StackedColumn,
    ChartType.Bar,
    ChartType.StackedBar,
    ChartType.Line,
    ChartType.Pie,
    ChartType.Area,
    ChartType.StackedArea,
    ChartType.Scatter,
    ChartType.Bubble,
    ChartType.Pareto,
    ChartType.Waterfall,
    ChartType.Treemap,
    ChartType.Heatmap,
    ChartType.LineColumn,
    ChartType.LineStackedColumn,
    ChartType.Funnel,
    ChartType.GeoArea,
    ChartType.GeoBubble,
    ChartType.GeoHeatmap,
    ChartType.PivotTable,
    ChartType.Sankey,
    ChartType.SpiderWeb,
    ChartType.Candlestick,
];
const muzePhase1Charts = [
    ChartType.AdvancedColumn,
    ChartType.AdvancedStackedColumn,
    ChartType.AdvancedLine,
    ChartType.AdvancedBar,
    ChartType.AdvancedStackedBar,
    ChartType.AdvancedLineColumn,
    ChartType.AdvancedArea,
    ChartType.AdvancedStackedArea,
    ChartType.AdvancedLineStackedColumn,
];
const muzePhase2Charts = [ChartType.AdvancedDonut, ChartType.AdvancedScatter, ChartType.AdvancedBubble, ChartType.AdvancedWaterfall, ChartType.AdvancedHeatmap, ChartType.AdvancedPareto];
let chartTypeNotSupportingGrowthQuery = [
    ChartType.Pie,
    ChartType.GeoArea,
    ChartType.GeoBubble,
    ChartType.GeoHeatmap,
];
const stackedChartTypes = [
    ChartType.StackedArea,
    ChartType.StackedColumn,
    ChartType.StackedBar,
    ChartType.LineStackedColumn,
];
const stackedVersionForChartType = {
    [ChartType.Area]: ChartType.StackedArea,
    [ChartType.Column]: ChartType.StackedColumn,
    [ChartType.Bar]: ChartType.StackedBar,
    [ChartType.LineColumn]: ChartType.LineStackedColumn,
};
const getMultiQueryChartTypes = () => {
    const multiQueryChartTypes = [
        ChartType.LineStackedColumn,
        ChartType.Sankey,
    ];
    if (isFetchPivotSummaryFromBackendEnabled()) {
        multiQueryChartTypes.push(ChartType.PivotTable);
    }
    return multiQueryChartTypes;
};
const highCardinalityDataPointSupportedCharts = {
    [ChartType.Column]: 1,
    [ChartType.StackedColumn]: 1,
    [ChartType.Bar]: 1,
    [ChartType.StackedBar]: 1,
    [ChartType.Line]: 1,
    [ChartType.Area]: 1,
    [ChartType.StackedArea]: 1,
    [ChartType.Scatter]: 1,
    [ChartType.LineColumn]: 1,
    [ChartType.LineStackedColumn]: 1,
    [ChartType.Kpi]: 1,
};
const tsHostedCustomCharts = [
    ChartType.AdvancedPivotTable,
    ChartType.MuzeStudio,
    ChartType.AdvancedColumn,
    ChartType.AdvancedStackedColumn,
    ChartType.AdvancedBar,
    ChartType.AdvancedStackedBar,
    ChartType.AdvancedLine,
    ChartType.AdvancedArea,
    ChartType.AdvancedStackedArea,
    ChartType.AdvancedLineColumn,
    ChartType.AdvancedLineStackedColumn,
    ChartType.AdvancedDonut,
    ChartType.AdvancedScatter,
    ChartType.AdvancedBubble,
    ChartType.AdvancedWaterfall,
    ChartType.AdvancedHeatmap,
    ChartType.AdvancedPareto,
];
const specImplementation = {};
chartTypeInDisplayOrderWithKpiEnabled = chartTypeInDisplayOrderWithKpiEnabled.concat([
    ChartType.GeoEarthArea,
    ChartType.GeoEarthBubble,
    ChartType.GeoEarthBar,
    ChartType.GeoEarthHeatmap,
    ChartType.GeoEarthGraph,
]);
chartTypeInDisplayOrder = chartTypeInDisplayOrder.concat([
    ChartType.GeoEarthArea,
    ChartType.GeoEarthBubble,
    ChartType.GeoEarthBar,
    ChartType.GeoEarthHeatmap,
    ChartType.GeoEarthGraph,
]);
chartTypeNotSupportingGrowthQuery = chartTypeNotSupportingGrowthQuery.concat([
    ChartType.GeoEarthArea,
    ChartType.GeoEarthBubble,
    ChartType.GeoEarthBar,
    ChartType.GeoEarthHeatmap,
    ChartType.GeoEarthGraph,
]);
function hasNativeLegend(chartModel) {
    const chartType = getChartType(chartModel);
    if (chartType === ChartType.Heatmap ||
        chartType === ChartType.Treemap ||
        chartType === ChartType.Sankey) {
        return true;
    }
    return false;
}
function isGeoEarthChartType(chartType) {
    return chartTypeByProvider[chartType] === chartProviders.GEO_EARTH;
}
function isGeoMapChartType(chartType) {
    return chartTypeByProvider[chartType] === chartProviders.GEO_MAP;
}
function isGeoChartType(chartType) {
    return isGeoMapChartType(chartType) || isGeoEarthChartType(chartType);
}
function isPivotChartType(chartType) {
    return chartTypeByProvider[chartType] === chartProviders.PIVOT_TABLE;
}
function isHighchartType(chartType) {
    return chartTypeByProvider[chartType] === chartProviders.HIGHCHART;
}
function isNetworkChartType(chartType) {
    return chartTypeByProvider[chartType] === chartProviders.NETWORK;
}
function getChartProvider(chartType, hasInsightData = false) {
    if (hasInsightData) {
        return chartProviders.INSIGHT;
    }
    return chartTypeByProvider[chartType];
}
function isXAxisVertical(chartType) {
    return [ChartType.Bar, ChartType.StackedBar].includes(chartType);
}
const ChartTypeUsingMeasureAsOrdinalOnYAxis = [ChartType.Heatmap];
function useMeasureOnYAxisAsOrdinal(chartType) {
    const ChartTypeUsingMeasureAsOrdinalMap = mapArrayToBooleanHash(ChartTypeUsingMeasureAsOrdinalOnYAxis);
    return ChartTypeUsingMeasureAsOrdinalMap[chartType];
}
function isAllowGradientColoring() {
    return (getEnvFlags().isEnhancedCfEnabled &&
        getEnvFlags().isGradientColoringEnabled);
}
const ChartTypeNeedOrdinalXAxis = [
    ChartType.Pie,
    ChartType.Heatmap,
    ChartType.Treemap,
    ChartType.Funnel,
    ChartType.SpiderWeb,
    ChartType.Pareto,
];
function useOrdinalXAxis(chartType) {
    const ChartTypeNeedOrdinalXAxisMap = mapArrayToBooleanHash(ChartTypeNeedOrdinalXAxis);
    return !!ChartTypeNeedOrdinalXAxisMap[chartType];
}
function hasPersistentLegend(chartType) {
    return chartType === ChartType.Heatmap || chartType === ChartType.Treemap;
}
function hasAxisLabels(chartType) {
    if (chartType === ChartType.Treemap ||
        chartType === ChartType.Pie ||
        chartType === ChartType.Funnel ||
        chartType === ChartType.SpiderWeb) {
        return false;
    }
    return true;
}
function getLegendLabelsFromLegendColumns(dataModel, chartModel) {
    var _a, _b, _c, _d;
    let legendColumnValues = getLegendColumnValues(dataModel);
    const isSortV2Enabled = getEnvFlags().isSortV2Enabled;
    if (getEnvFlags().isCustomSortingEnabled &&
        isCustomSortSupportedForChartType(getChartType(chartModel))) {
        const customSortOrder = getVizColumnsSortOrder(chartModel.columns, chartModel.sortInfo, ((_b = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.data) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.columnDataLite) || [], true);
        const columnIdsInSequenceWithData = ((_d = (_c = chartModel.data) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.columnDataLite.map(column => column.columnId)) || [];
        const sortOrderPriority = getColumnSortPriorityOrder(chartModel.sortInfo, columnIdsInSequenceWithData, getEnvFlags().isSortV2Enabled
            ? chartModel.config.axisConfig[0].color
            : null);
        legendColumnValues = customSortOnSeriesData(legendColumnValues, getCustomOrderInSequenceWithoutColumnId(columnIdsInSequenceWithData, customSortOrder), sortOrderPriority);
    }
    return legendColumnValues.map(label => {
        return { label, id: String(label).toLowerCase() };
    });
}
function getLegendLabelsFromYAxisColumns(yAxisColumns) {
    return yAxisColumns.map(column => {
        return {
            label: getName(column),
            id: getId(column),
        };
    });
}
function _getLegendLabels(chartModel, dataModel) {
    const legendColumns = getLegendColumns(chartModel);
    if (legendColumns.length > 0) {
        return getLegendLabelsFromLegendColumns(dataModel, chartModel);
    }
    return getLegendLabelsFromYAxisColumns(getYAxisColumns(chartModel));
}
function getLegendLabels(chartModel, dataModel) {
    var _a;
    if (((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData) === null || _a === void 0 ? void 0 : _a.forecasts) && isSpotIQV2Enabled()) {
        const _labels = [
            {
                id: 'Forecast',
                label: getTranslatedTextCallback()('FORECAST_TITLE'),
            },
        ];
        return _labels;
    }
    return specImplementation[getChartType(chartModel)].getLegendLabels
        ? specImplementation[getChartType(chartModel)].getLegendLabels(chartModel, dataModel)
        : _getLegendLabels(chartModel, dataModel);
}
export function containsAttributeColumn(columns) {
    return _.some(columns, column => isAttribute(column));
}
export function isCustomPropDisplayed(customProp, chartModel) {
    if (_.isEmpty(customProp.constraints)) {
        return true;
    }
    if (isSinglePointKPIEnabledWithValue(chartModel) &&
        customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.SINGLE_POINT_KPI_ENABLED)) {
        return true;
    }
    if (customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.NON_DESCENDING_TIME_COLUMN_ON_X)) {
        const xAxisColumns = getXAxisColumns(chartModel);
        if (_.isEmpty(xAxisColumns) ||
            !areAllTimeSeriesColumns(xAxisColumns) ||
            _.some(xAxisColumns, xAxisColumn => isDescendingSort(chartModel, xAxisColumn))) {
            return false;
        }
    }
    if (isKPIChartOnAttributesEnabled() &&
        customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.NON_ATTRIBUTE_COLUMN_ON_Y) &&
        containsAttributeColumn(getYAxisColumns(chartModel))) {
        return false;
    }
    if (customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.SHOW_SPARKLINE_ENABLED)) {
        if (!isSparklineEnabled(chartModel)) {
            return false;
        }
    }
    if (customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.SHOW_ANOMALIES_ENABLED)) {
        if (!isShowAnomaliesSupported(chartModel) ||
            !isShowAnomaliesEnabled(chartModel)) {
            return false;
        }
    }
    if (customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.COMPARISON_ENABLED)) {
        if (!isComparisionEnabled(chartModel)) {
            return false;
        }
    }
    if (customProp.constraints.includes(CUSTOM_PROP_CONSTRAINTS.SHOW_ANALYSE_CHANGE_CTA_ENABLED)) {
        if (!isComparisionEnabled(chartModel) ||
            !isAnalyseChangeCtaEnabled(chartModel)) {
            return false;
        }
    }
    return true;
}
const timeBucketsInOrder = {
    [dateUtil.timeBuckets.NO_BUCKET]: 0,
    [dateUtil.timeBuckets.HOURLY]: 1,
    [dateUtil.timeBuckets.DAILY]: 2,
    [dateUtil.timeBuckets.DAY_OF_YEAR]: 3,
    [dateUtil.timeBuckets.DAY_OF_QUARTER]: 4,
    [dateUtil.timeBuckets.DAY_OF_MONTH]: 5,
    [dateUtil.timeBuckets.DAY_OF_WEEK]: 6,
    [dateUtil.timeBuckets.WEEKLY]: 7,
    [dateUtil.timeBuckets.WEEK_OF_YEAR]: 8,
    [dateUtil.timeBuckets.WEEK_OF_QUARTER]: 9,
    [dateUtil.timeBuckets.WEEK_OF_MONTH]: 10,
    [dateUtil.timeBuckets.MONTHLY]: 11,
    [dateUtil.timeBuckets.MONTH_OF_YEAR]: 12,
    [dateUtil.timeBuckets.MONTH_OF_QUARTER]: 13,
    [dateUtil.timeBuckets.QUARTERLY]: 14,
    [dateUtil.timeBuckets.QUARTER_OF_YEAR]: 15,
    [dateUtil.timeBuckets.YEARLY]: 16,
};
export const isCustomChart = (chartType) => {
    return chartType === ChartType.CustomChart;
};
export const isTsHostedChart = (chartType) => {
    return tsHostedCustomCharts.includes(chartType);
};
export const isCustomOrTsHostedChart = (chartType) => {
    return isCustomChart(chartType) || isTsHostedChart(chartType);
};
const isMuzeChartsEnabled = () => {
    return muzeChartPhase1Enabled();
};
export const isCustomOrTsHostedChartEnabled = (chartType) => {
    const IS_CHART_TYPE_ENABLED = {
        [ChartType.CustomChart]: isCustomChartEnabled(),
        [ChartType.AdvancedPivotTable]: isAdvancedPivotTableEnabled(),
        [ChartType.AdvancedColumn]: isMuzeChartsEnabled(),
        [ChartType.AdvancedStackedColumn]: isMuzeChartsEnabled(),
        [ChartType.AdvancedBar]: isMuzeChartsEnabled(),
        [ChartType.AdvancedStackedBar]: isMuzeChartsEnabled(),
        [ChartType.MuzeStudio]: enableMuzeStudio() || false,
        [ChartType.AdvancedLine]: isMuzeChartsEnabled(),
        [ChartType.AdvancedArea]: isMuzeChartsEnabled(),
        [ChartType.AdvancedStackedArea]: isMuzeChartsEnabled(),
        [ChartType.AdvancedLineColumn]: isMuzeChartsEnabled(),
        [ChartType.AdvancedLineStackedColumn]: isMuzeChartsEnabled(),
        [ChartType.AdvancedDonut]: muzeDonutChartEnabled(),
        [ChartType.AdvancedScatter]: muzeScatterChartEnabled(),
        [ChartType.AdvancedBubble]: muzeBubbleChartEnabled(),
        [ChartType.AdvancedWaterfall]: muzeWaterfallChartEnabled(),
        [ChartType.AdvancedHeatmap]: muzeHeatmapChartEnabled(),
        [ChartType.AdvancedPareto]: muzeParetoChartEnabled(),
    };
    return (isCustomOrTsHostedChart(chartType) && IS_CHART_TYPE_ENABLED[chartType]);
};
const chartTypeSwaps = [[ChartType.PivotTable, ChartType.AdvancedPivotTable]];
const muzeChartRegistry = [
    { classic: ChartType.Column, advanced: ChartType.AdvancedColumn, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.StackedColumn, advanced: ChartType.AdvancedStackedColumn, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.Bar, advanced: ChartType.AdvancedBar, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.StackedBar, advanced: ChartType.AdvancedStackedBar, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.Line, advanced: ChartType.AdvancedLine, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.Area, advanced: ChartType.AdvancedArea, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.StackedArea, advanced: ChartType.AdvancedStackedArea, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.LineColumn, advanced: ChartType.AdvancedLineColumn, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.LineStackedColumn, advanced: ChartType.AdvancedLineStackedColumn, isEnabled: muzeChartPhase1Enabled },
    { classic: ChartType.Pie, advanced: ChartType.AdvancedDonut, isEnabled: muzeDonutChartEnabled },
    { classic: ChartType.Scatter, advanced: ChartType.AdvancedScatter, isEnabled: muzeScatterChartEnabled },
    { classic: ChartType.Bubble, advanced: ChartType.AdvancedBubble, isEnabled: muzeBubbleChartEnabled },
    { classic: ChartType.Waterfall, advanced: ChartType.AdvancedWaterfall, isEnabled: muzeWaterfallChartEnabled },
    { classic: ChartType.Heatmap, advanced: ChartType.AdvancedHeatmap, isEnabled: muzeHeatmapChartEnabled },
    { classic: ChartType.Pareto, advanced: ChartType.AdvancedPareto, isEnabled: muzeParetoChartEnabled },
];
export const getClassicExperienceChartTypes = () => muzeChartRegistry
    .filter(({ isEnabled }) => isEnabled())
    .map(({ classic }) => classic);
export const getNewExperienceChartTypes = () => muzeChartRegistry
    .filter(({ isEnabled }) => isEnabled())
    .map(({ advanced }) => advanced);
export const newExperienceChartTypes = muzeChartRegistry.map(({ advanced }) => advanced);
export const classicExperienceChartTypes = muzeChartRegistry.map(({ classic }) => classic);
export const getEquivalentChartType = (chartType) => {
    const entry = muzeChartRegistry.find(e => e.classic === chartType || e.advanced === chartType);
    if (!entry)
        return undefined;
    return chartType === entry.classic ? entry.advanced : entry.classic;
};
export const shouldShowSwitchNewChartActionMenu = (chartType) => {
    return (isMuzeChartsEnabled() &&
        getClassicExperienceChartTypes().includes(chartType));
};
export const shouldShowSwitchClassicChartActionMenu = (chartType) => {
    const isSwitcherEnabled = newExperienceChartTypes.includes(chartType);
    return isSwitcherEnabled;
};
const configOptions = (chartType, chartModel, customChartConfiguration) => {
    var _a;
    const chartTypeSpecImplementation = specImplementation[chartType];
    let options = {};
    if (_.isFunction(chartTypeSpecImplementation.configOptions)) {
        if (isCustomOrTsHostedChart(chartType)) {
            options = chartTypeSpecImplementation.configOptions(customChartConfiguration);
        }
        else {
            options = chartTypeSpecImplementation.configOptions(chartModel);
        }
    }
    if ((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.customOverridesConfig) === null || _a === void 0 ? void 0 : _a.isDataLabelsEnabledByDefault) {
        options.dataLabelsEnabledByDefault = true;
    }
    if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
        options = pointSelectionServiceConfig.updateHoverAndSelectState(chartType, options);
    }
    return new ChartConfigOptions(options);
};
const getCFAllowedDimensionForTSHostedCharts = (chartType) => {
    var _a;
    const chartTypeSpecImplementation = specImplementation[chartType];
    return ((_a = chartTypeSpecImplementation.CFAllowedDimensionsForTSHostedCustomChart) !== null && _a !== void 0 ? _a : []);
};
const getDimensionHandlingMappingForTSHostedCharts = (chartType) => {
    var _a;
    const chartTypeSpecImplementation = specImplementation[chartType];
    return (_a = chartTypeSpecImplementation.customToAxisConfigKeyMapping) !== null && _a !== void 0 ? _a : [];
};
const getCustomAxisConfigToCustomChartConfigTransformerForTSHostedCharts = (chartType) => {
    const chartTypeSpecImplementation = specImplementation[chartType];
    return chartTypeSpecImplementation.customAxisConfigToCustomChartConfigTransformer;
};
export const isConditionalFormattingAllowedForChart = (chartType) => {
    const chartTypeSpecImplementation = specImplementation[chartType];
    const allowedConfigs = chartTypeSpecImplementation.configOptions(null)
        .allowedConfigurations;
    if (_.isNil(allowedConfigs === null || allowedConfigs === void 0 ? void 0 : allowedConfigs.conditionalFormatting)) {
        return true;
    }
    return allowedConfigs.conditionalFormatting;
};
export const isYAxisSelectorDisabled = (chartType) => chartType === ChartType.GridTable;
const LINE_STYLE_CUSTOMISATION_CHART_TYPES = [
    ChartType.Line,
    ChartType.Area,
    ChartType.StackedArea,
    ChartType.LineColumn,
    ChartType.LineStackedColumn,
];
const LINE_MARKER_CUSTOMISATION_CHART_TYPES = [
    ChartType.Line,
    ChartType.LineColumn,
    ChartType.LineStackedColumn,
];
const MARKER_TYPE_CUSTOMISATION_CHART_TYPES = [
    ChartType.Line,
    ChartType.LineColumn,
    ChartType.LineStackedColumn,
    ChartType.Scatter,
];
export const supportsLineStyleColumnCustomisation = (chartType) => LINE_STYLE_CUSTOMISATION_CHART_TYPES.includes(chartType);
export const supportsLineMarkerColumnCustomisation = (chartType) => LINE_MARKER_CUSTOMISATION_CHART_TYPES.includes(chartType);
export const supportsMarkerTypeColumnCustomisation = (chartType) => MARKER_TYPE_CUSTOMISATION_CHART_TYPES.includes(chartType);
export const supportsMarkerSizeColumnCustomisation = (chartType) => chartType === ChartType.Scatter;
const validateAxisConfig = (chartType, axisConfig, chartViz, getTranslatedText) => {
    const chartConfigOptions = configOptions(chartType, null);
    const chartAxisConfigValidity = {
        isValid: true,
        errors: {},
    };
    if (isCustomChart(chartType)) {
        chartAxisConfigValidity.isValid = true;
        return chartAxisConfigValidity;
    }
    if (isTsHostedChart(chartType)) {
        const chartTypeSpecImplementation = specImplementation[chartType];
        chartAxisConfigValidity.isValid = true;
        if (chartTypeSpecImplementation.isChartTypeEnabled) {
            chartAxisConfigValidity.isValid = chartTypeSpecImplementation.isChartTypeEnabled(chartViz);
        }
        return chartAxisConfigValidity;
    }
    const generalValidity = validateAxisColumns(axisConfig, chartType, chartViz, chartConfigOptions.allowedConfigurations.shouldOnlyContainOneYAxis, chartConfigOptions.radialEnabled, isYAxisSelectorDisabled(chartType), chartConfigOptions.allowedConfigurations.transposeXY, chartConfigOptions.categoryAxisEnabled, getTranslatedText);
    const invalid = _.values(generalValidity).some(val => val);
    if (invalid) {
        chartAxisConfigValidity.isValid = false;
        chartAxisConfigValidity.errors = generalValidity;
    }
    else {
        const chartTypeSpecImplementation = specImplementation[chartType];
        const chartTypeValidation = chartTypeSpecImplementation.validateAxisConfig(axisConfig, getTranslatedText, chartViz);
        chartAxisConfigValidity.isValid = chartTypeValidation.isValid;
        if (!chartTypeValidation.isValid && chartTypeValidation.errors) {
            chartAxisConfigValidity.errors = chartTypeValidation.errors;
        }
    }
    return chartAxisConfigValidity;
};
const seriesOverride = function (chartType, chartModel, dataModel) {
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (_.isFunction(chartTypeSpecImplementation.seriesOverride)) {
        chartTypeSpecImplementation.seriesOverride(chartModel, dataModel);
    }
};
const customColorSelectorConfig = (chartType, chartModel, onColorSelect, getTranslatedText) => {
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (_.isFunction(chartTypeSpecImplementation.customColorSelectorConfig)) {
        return chartTypeSpecImplementation.customColorSelectorConfig(chartModel, onColorSelect, getTranslatedText);
    }
    return [];
};
export const getChartsWithDataLabelsEnabledByDefault = () => {
    return Object.keys(specImplementation).filter(chartType => {
        var _a, _b;
        return ((_b = (_a = specImplementation[chartType]) === null || _a === void 0 ? void 0 : _a.configOptions) === null || _b === void 0 ? void 0 : _b.call(_a).dataLabelsEnabledByDefault) && chartType;
    });
};
const shouldShowDataLabelToggleForYAxisColumns = function (chartModel, columnId) {
    return isYAxisColumn(chartModel, columnId);
};
const shouldShowStackLabelToggleForYAxisColumns = (chartModel, columnId) => {
    return isYAxisColumn(chartModel, columnId);
};
const shouldShowDetailLabelToggleForYAxisColumns = (chartModel, columnId) => {
    return isYAxisColumn(chartModel, columnId);
};
const shouldShowDetailLabelToggleForColumn = (chartModel, columnId) => {
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    if (!chartConfigOptions.allowedConfigurations.showDetailedLabels) {
        return false;
    }
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (_.isFunction(chartTypeSpecImplementation.shouldShowDetailLabelToggleForColumn)) {
        return chartTypeSpecImplementation.shouldShowDetailLabelToggleForColumn(chartModel, columnId);
    }
    logger.error(`implement shouldShowDetailLabelToggleForColumn for ${chartType}`);
    return undefined;
};
const shouldShowStackLabelToggleForColumn = (chartModel, columnId) => {
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    if (!chartConfigOptions.allowedConfigurations.showStackLabels) {
        return false;
    }
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (_.isFunction(chartTypeSpecImplementation.shouldShowStackLabelToggleForColumn)) {
        return chartTypeSpecImplementation.shouldShowStackLabelToggleForColumn(chartModel, columnId);
    }
    logger.error(`implement shouldShowStackLabelToggleForColumn for ${chartType}`);
    return undefined;
};
const shouldShowDataLabelToggleForColumn = (chartModel, columnId) => {
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    if (!chartConfigOptions.allowedConfigurations.showDataLabels) {
        return false;
    }
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (_.isFunction(chartTypeSpecImplementation.shouldShowDataLabelToggleForColumn)) {
        if (chartType === ChartType.Heatmap) {
            const toggledColumnId = getId(getRadialColumn(chartModel));
            return chartTypeSpecImplementation.shouldShowDataLabelToggleForColumn(chartModel, toggledColumnId);
        }
        return chartTypeSpecImplementation.shouldShowDataLabelToggleForColumn(chartModel, columnId);
    }
    logger.error(`implement shouldShowDataLabelToggleForColumn for ${chartType}`);
    return undefined;
};
const getChartSpecificCardinalityError = (chartModel, dataModel) => {
    const chartType = getChartType(chartModel);
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (_.isFunction(chartTypeSpecImplementation.getLegendCardinalityError)) {
        return chartTypeSpecImplementation.getLegendCardinalityError(chartModel);
    }
    return null;
};
const getChartSpecificDataValidationError = (chartModel, dataModel) => {
    const chartType = getChartType(chartModel);
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (!_.isFunction(chartTypeSpecImplementation.getChartDataValidationError)) {
        return null;
    }
    return chartTypeSpecImplementation.getChartDataValidationError(chartModel, dataModel);
};
const getChartTooltip = (chartType, getTranslatedText) => {
    let disableDetails = getTranslatedText(`disabledChartTypeSelectorTooltips.${chartType}`);
    if (chartType === ChartType.PivotTable &&
        enableAttributesInPivotDataArea()) {
        disableDetails = getTranslatedText('disabledChartTypeSelectorTooltips.PIVOT_TABLE_ENABLE_ATTRIBUTE_FLAG');
    }
    if (!disableDetails) {
        logger.error('No disabled tooltip defined for chart type:', chartType);
    }
    if (chartType === ChartType.Pie) {
        disableDetails = assign(disableDetails, chartSpecificConstants.PIECHART_CARDINALITY_THRESHOLD);
    }
    return disableDetails;
};
export const getEffectiveYAxisColumn = (chartModel, pointData, getTranslatedText) => {
    const chartType = getChartType(chartModel);
    if (_.isFunction(specImplementation[chartType].getEffectiveYAxisColumn)) {
        return specImplementation[chartType].getEffectiveYAxisColumn(chartModel, pointData, getTranslatedText);
    }
    const yAxisColumns = getYAxisColumns(chartModel);
    let yAxisColumn;
    if (yAxisColumns.length > 1 &&
        !!pointData.series &&
        !!pointData.series.name) {
        yAxisColumn = getYAxisColumn(chartModel, pointData.series.name);
    }
    else {
        yAxisColumn = yAxisColumns[0];
    }
    return yAxisColumn;
};
function containsGrowthColumn(columns) {
    return _.some(columns, function (column) {
        return isGrowth(column);
    });
}
export const DEFAULT_MIN_POINT_LENGTH = 0;
export const VISIBLE_MIN_POINT_LENGTH = 3;
export const getMinBarHeight = (isMinimumBarHeightEnabled) => {
    return isMinimumBarHeightEnabled
        ? VISIBLE_MIN_POINT_LENGTH
        : DEFAULT_MIN_POINT_LENGTH;
};
export const incompleteZoneConfigTemplate1 = {
    getIncompleteColor(series) {
        return chroma(series.color)
            .alpha(0.6)
            .css();
    },
    getIncompleteThreshold(timeBucket) {
        var _a;
        if (!((_a = dateUtil.luxonAddUnits) === null || _a === void 0 ? void 0 : _a[timeBucket])) {
            return Number.MAX_SAFE_INTEGER;
        }
        const start = dateUtil.getTimeBucketStart(Date.now(), timeBucket);
        return dateUtil.subtractFromField(start, timeBucket, 1);
    },
};
function areAllEffectivelyNumeric(columns) {
    return columns.every(function (column) {
        return isEffectivelyNumeric(column);
    });
}
function areAllEffectivelyNonNumeric(columns) {
    return columns.every(function (column) {
        return !isEffectivelyNumeric(column);
    });
}
function areAllAttributes(columns) {
    return columns.every(function (column) {
        return isAttribute(column);
    });
}
function getHighchartsType(chartType) {
    const chartTypeSpecImplementation = specImplementation[chartType];
    return chartTypeSpecImplementation.highchartsType || chartType;
}
function getDefaultBatchSize(chartType) {
    const chartTypeSpecImplementation = specImplementation[chartType];
    return chartTypeSpecImplementation.configOptions(null).batchSize;
}
const createAdvancedChartConfig = (customToAxisMapping, config) => {
    return {
        ...specImplementation[ChartType.CustomChart],
        CFAllowedDimensionsForTSHostedCustomChart: config.cfAllowedDimensions,
        configOptions: (customChartConfiguration) => {
            var _a;
            const allowedConfigurationsFromCustomChart = specImplementation[ChartType.CustomChart].configOptions(customChartConfiguration);
            allowedConfigurationsFromCustomChart.allowedConfigurations.conditionalFormatEditorParams = {
                isPlotAsBand: true,
                isFont: false,
                isBackgroundColorPickerAvailable: true,
                isConditionalFormattingAllowedOnAttributeColumn: (_a = config.isConditionalFormattingAllowedOnAttributeColumn) !== null && _a !== void 0 ? _a : true,
            };
            allowedConfigurationsFromCustomChart.allowedConfigurations.gradientColoring = false;
            return {
                ...allowedConfigurationsFromCustomChart,
                batchSize: 5000,
            };
        },
        customToAxisConfigKeyMapping: customToAxisMapping,
        customAxisConfigToCustomChartConfigTransformer: config.customAxisConfigToCustomChartConfigTransformer,
        isChartTypeEnabled: config.isChartTypeEnabled || defaultAdvancedChartTypeEnabler,
    };
};
const DEFAULT_CUSTOM_CHART_BATCH_SIZE = 20000;
specImplementation[ChartType.CustomChart] = {
    validateAxisConfig: (axisConfig) => ({ isValid: true }),
    configOptions: (customChartConfiguration) => {
        var _a;
        return {
            allowedConfigurations: {
                allowNumberFormatting: !!(customChartConfiguration === null || customChartConfiguration === void 0 ? void 0 : customChartConfiguration.allowColumnNumberFormatting),
                allowColorChange: false,
                conditionalFormatting: !!(customChartConfiguration === null || customChartConfiguration === void 0 ? void 0 : customChartConfiguration.allowColumnConditionalFormatting),
                allowSortFormatOption: false,
                allowChartCustomisation: false,
                allowDynamicSettings: true,
                gradientEnabled: !!(customChartConfiguration === null || customChartConfiguration === void 0 ? void 0 : customChartConfiguration.allowGradientColoring) &&
                    isAllowGradientColoring(),
                advanced: {
                    display: {
                        tooltipCustomization: true,
                    },
                },
            },
            batchSize: (_a = getEnvFlags().customChartRowsLimit) !== null && _a !== void 0 ? _a : DEFAULT_CUSTOM_CHART_BATCH_SIZE,
        };
    },
    shouldShowDataLabelToggleForColumn: () => false,
};
specImplementation[ChartType.AdvancedPivotTable] = {
    ...specImplementation[ChartType.CustomChart],
    configOptions: (customChartConfiguration) => {
        const allowedConfigurationsFromCustomChart = specImplementation[ChartType.CustomChart].configOptions(customChartConfiguration);
        allowedConfigurationsFromCustomChart.allowedConfigurations.conditionalFormatEditorParams = {
            isPlotAsBand: false,
            isFont: true,
            isBackgroundColorPickerAvailable: true,
            isConditionalFormattingAllowedOnAttributeColumn: true,
        };
        allowedConfigurationsFromCustomChart.allowedConfigurations.gradientColoring = false;
        return {
            ...allowedConfigurationsFromCustomChart,
            batchSize: 100001,
        };
    },
    CFAllowedDimensionsForTSHostedCustomChart: ['values'],
    customToAxisConfigKeyMapping: [
        ['rows', 'x'],
        ['values', 'y'],
        ['columns', 'color'],
        ['split-by-color', ''],
    ],
};
const defaultAdvancedChartTypeEnabler = (chartViz) => {
    if (!chartViz)
        return false;
    const allSageColumns = getSageColumns(chartViz);
    const hasAttributes = allSageColumns.some(isAttribute);
    const hasMeasures = allSageColumns.some(isMeasure);
    return hasAttributes && hasMeasures;
};
specImplementation[ChartType.AdvancedBar] = createAdvancedChartConfig(DEFAULT_ADVANCED_BAR_CHART_MAPPING, {
    cfAllowedDimensions: ['x-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedColumn] = createAdvancedChartConfig(DEFAULT_ADVANCED_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedStackedBar] = createAdvancedChartConfig(DEFAULT_ADVANCED_BAR_CHART_MAPPING, {
    cfAllowedDimensions: ['x-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_HORIZONTAL_CHARTS,
});
specImplementation[ChartType.AdvancedStackedColumn] = createAdvancedChartConfig(DEFAULT_ADVANCED_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedLine] = createAdvancedChartConfig(DEFAULT_ADVANCED_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedArea] = createAdvancedChartConfig(DEFAULT_ADVANCED_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedStackedArea] = createAdvancedChartConfig(DEFAULT_ADVANCED_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedLineColumn] = createAdvancedChartConfig(DEFAULT_ADVANCED_LINE_COLUMN_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis-line', 'y-axis-column'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_LINE_COLUMN_CHART,
    isChartTypeEnabled: (chartViz) => {
        const isDefaultAdvancedChartTypeEnabled = defaultAdvancedChartTypeEnabler(chartViz);
        if (!isDefaultAdvancedChartTypeEnabled) {
            return false;
        }
        const allSageColumns = getSageColumns(chartViz);
        const measureColumns = allSageColumns.filter(column => isMeasure(column));
        if (measureColumns.length < MIN_MEASURES_FOR_LINE_COLUMN_CHART) {
            return false;
        }
        return true;
    },
});
specImplementation[ChartType.AdvancedLineStackedColumn] = {
    ...specImplementation[ChartType.AdvancedLineColumn],
};
specImplementation[ChartType.MuzeStudio] = {
    ...specImplementation[ChartType.CustomChart],
    customToAxisConfigKeyMapping: [
        ['fields', 'x'],
        ['fields', 'y'],
    ],
};
specImplementation[ChartType.AdvancedDonut] = createAdvancedChartConfig(DEFAULT_ADVANCED_DONUT_CHART_MAPPING, {
    cfAllowedDimensions: [],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedWaterfall] = createAdvancedChartConfig(DEFAULT_ADVANCED_WATERFALL_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
});
specImplementation[ChartType.AdvancedScatter] = createAdvancedChartConfig(DEFAULT_ADVANCED_SCATTER_CHART_MAPPING, {
    cfAllowedDimensions: ['x-axis', 'y-axis'],
    isConditionalFormattingAllowedOnAttributeColumn: false,
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
    isChartTypeEnabled: (chartViz) => {
        const allSageColumns = getSageColumns(chartViz);
        const attributeColumns = allSageColumns.filter(isAttribute);
        const measureColumns = allSageColumns.filter(isMeasure);
        return attributeColumns.length >= 2 || (attributeColumns.length >= 1 && measureColumns.length >= 1);
    },
});
specImplementation[ChartType.AdvancedBubble] = createAdvancedChartConfig(DEFAULT_ADVANCED_BUBBLE_CHART_MAPPING, {
    cfAllowedDimensions: ['x-axis', 'y-axis'],
    isConditionalFormattingAllowedOnAttributeColumn: false,
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
    isChartTypeEnabled: (chartViz) => {
        const allSageColumns = getSageColumns(chartViz);
        const attributeColumns = allSageColumns.filter(isAttribute);
        const measureColumns = allSageColumns.filter(isMeasure);
        return attributeColumns.length >= 1 && measureColumns.length >= 2;
    },
});
specImplementation[ChartType.AdvancedHeatmap] = createAdvancedChartConfig(DEFAULT_ADVANCED_HEATMAP_CHART_MAPPING, {
    cfAllowedDimensions: ['value'],
    isConditionalFormattingAllowedOnAttributeColumn: false,
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_HEATMAP_CHART,
    isChartTypeEnabled: (chartViz) => {
        const allSageColumns = getSageColumns(chartViz);
        const attributeColumns = allSageColumns.filter(isAttribute);
        const measureColumns = allSageColumns.filter(isMeasure);
        return attributeColumns.length >= 2 && measureColumns.length >= 1;
    },
});
specImplementation[ChartType.AdvancedPareto] = createAdvancedChartConfig(DEFAULT_ADVANCED_PARETO_CHART_MAPPING, {
    cfAllowedDimensions: ['y-axis'],
    customAxisConfigToCustomChartConfigTransformer: DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER,
    isChartTypeEnabled: (chartViz) => {
        const allSageColumns = getSageColumns(chartViz);
        const attributeColumns = allSageColumns.filter(isAttribute);
        const measureColumns = allSageColumns.filter(isMeasure);
        return attributeColumns.length >= 1 && measureColumns.length >= 1;
    },
});
function disableDefaultGridlines(chartType, axisConfigs) {
    return {
        xGridlineEnabled: false,
        yGridlineEnabled: false,
    };
}
function getChartBasedGridLineConfig(chartType, axisConfigs) {
    if (specImplementation[chartType].getGridLineConfig) {
        return specImplementation[chartType].getGridLineConfig(chartType, axisConfigs);
    }
    return {
        xGridlineEnabled: false,
        yGridlineEnabled: false,
    };
}
function getGridLineConfig(chartType, axisConfigs) {
    const axisConfig = axisConfigs[0];
    if (axisConfig && axisConfig.x.length && axisConfig.y.length) {
        if (axisConfig.y.length > 1 ||
            (axisConfig.y.length === 1 && axisConfig.color.length > 0)) {
            return {
                xGridlineEnabled: true,
                yGridlineEnabled: false,
            };
        }
        if (axisConfigs.length > 1) {
            return {
                xGridlineEnabled: true,
                yGridlineEnabled: false,
            };
        }
    }
    return {
        xGridlineEnabled: false,
        yGridlineEnabled: false,
    };
}
function translateAxisConfigWithMeasureNamesValues(chartModel) {
    const chartSpec = specImplementation[getChartType(chartModel)];
    if (_.isFunction(chartSpec.translateAxisConfigWithMeasureNamesValues)) {
        return chartSpec.translateAxisConfigWithMeasureNamesValues(chartModel);
    }
    return chartModel.config.axisConfig;
}
function translateQueryDefinitionWithMeasureNamesValues(axisConfigs, chartModel, chartType) {
    const chartSpec = specImplementation[chartType];
    if (_.isFunction(chartSpec.translateQueryDefinitionWithMeasureNamesValues)) {
        return chartSpec.translateQueryDefinitionWithMeasureNamesValues(axisConfigs, chartModel);
    }
    return axisConfigs;
}
specImplementation[ChartType.Bar] = {};
specImplementation[ChartType.Bar].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length && !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Bar].configOptions = (chartModel) => {
    const hasMultipleSeries = chartModel && getYAxisColumns(chartModel).length > 1;
    const groupPadding = hasMultipleSeries ? 0.02 : 0;
    const borderWidth = hasMultipleSeries ? 0 : 0.5;
    const config = {
        highcharts: {
            xAxis: {
                labels: {
                    y: 4,
                    style: {
                        whiteSpace: 'wrap',
                        textOverflow: 'ellipsis',
                    },
                },
            },
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    borderWidth,
                    pointPadding: 0,
                    groupPadding,
                },
            },
        },
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    regressionLine: true,
                    responsiveLayout: true,
                    showNullAsZero: true,
                },
            },
            excludeNullValues: true,
            zoomPanStateToggle: true,
            yAxisRange: true,
            regressionLine: true,
            transposeXY: true,
            showRoundedNumbers: true,
        },
        minPointLengthSupported: true,
    };
    return config;
};
specImplementation[ChartType.Bar].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Bar].getGridLineConfig = getGridLineConfig;
specImplementation[ChartType.Column] = {};
specImplementation[ChartType.Column].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length &&
        areAllAttributes(axisConfig.x) &&
        axisConfig.y.length &&
        !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Column].configOptions = (chartModel) => {
    const hasMultipleSeries = chartModel && getYAxisColumns(chartModel).length > 1;
    const groupPadding = hasMultipleSeries ? 0.02 : 0;
    const borderWidth = hasMultipleSeries ? 0 : 0.5;
    const config = {
        highcharts: {
            plotOptions: {
                column: {
                    allowPointSelect: isContextualChangeAnalysisEnabled(),
                },
                series: {
                    pointPadding: 0.1,
                    groupPadding,
                    borderWidth,
                },
            },
        },
        hasStackedPointsForMultiSeries: true,
        minPointLengthSupported: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    responsiveLayout: true,
                    regressionLine: true,
                    showNullAsZero: true,
                },
            },
            excludeNullValues: true,
            zoomPanStateToggle: true,
            yAxisRange: true,
            regressionLine: true,
            showRoundedNumbers: true,
        },
    };
    return config;
};
specImplementation[ChartType.Column].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Column].getGridLineConfig = getGridLineConfig;
specImplementation[ChartType.StackedColumn] = {};
specImplementation[ChartType.StackedColumn].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    const hasSingleYAxis = axisConfig.y.length === 1;
    const colorColumns = axisConfig.color || [];
    const hasMultipleYAxisAndNoLegends = axisConfig.y.length > 1 && colorColumns.length === 0;
    if (axisConfig.x.length &&
        areAllAttributes(axisConfig.x) &&
        (hasSingleYAxis || hasMultipleYAxisAndNoLegends) &&
        !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.StackedColumn].configOptions = (chartModel) => {
    const config = {
        highcharts: {
            chart: {
                spacingTop: 20,
            },
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    crisp: false,
                    stacking: !!chartModel && isYAxisStackedAsPercent(chartModel)
                        ? 'percent'
                        : 'normal',
                    groupPadding: 0,
                    pointPadding: 0.1,
                    dataLabels: {},
                    fillOpacity: 1,
                },
            },
            yAxis: [
                {
                    stackLabels: {
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.COLUMN_CHART, getEnvFlags().applyContrastForDataLabels),
                        useHTML: false,
                    },
                },
            ],
        },
        hasStackedPointsForMultiSeries: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    responsiveLayout: true,
                    showNullAsZero: true,
                },
            },
            excludeNullValues: true,
            showYAxisAsPercent: true,
            zoomPanStateToggle: true,
            yAxisRange: true,
            showDataLabels: false,
            showStackLabels: true,
            showDetailedLabels: true,
            showRoundedNumbers: true,
            showYAxisLinkingOption: false,
            shouldOnlyContainOneYAxis: true,
        },
        minPointLengthSupported: true,
    };
    if (chartModel && isYAxisStackedAsPercent(chartModel)) {
        const valueColumn = _.first(getYAxisColumns(chartModel));
        config.highcharts.yAxis = [
            {
                labels: {
                    formatter() {
                        if (this.isTooltip) {
                            return getFormattedValueForNumericColumn(this.value, valueColumn);
                        }
                        return formatDataValue(this.value / 100, {
                            nDecimal: getStackLabelDecimalPrecision(chartModel),
                            isPercent: true,
                        });
                    },
                },
            },
        ];
    }
    return config;
};
specImplementation[ChartType.StackedColumn].highchartsType = ChartType.Column;
specImplementation[ChartType.StackedColumn].getYSEOPConfig =
    specImplementation[ChartType.Column].getYSEOPConfig;
function getDefaultStackingValueColumn(queryDefinitions) {
    return _.first(queryDefinitions[0].yAxisColumns);
}
specImplementation[ChartType.StackedColumn].getStackedValuesColumn = getDefaultStackingValueColumn;
specImplementation[ChartType.StackedColumn].shouldShowDetailLabelToggleForColumn = shouldShowDetailLabelToggleForYAxisColumns;
specImplementation[ChartType.StackedColumn].shouldShowStackLabelToggleForColumn = shouldShowStackLabelToggleForYAxisColumns;
specImplementation[ChartType.StackedColumn].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.StackedBar] = {};
specImplementation[ChartType.StackedBar].getStackedValuesColumn = getDefaultStackingValueColumn;
specImplementation[ChartType.StackedBar].highchartsType = ChartType.Bar;
specImplementation[ChartType.StackedBar].getYSEOPConfig =
    specImplementation[ChartType.StackedColumn].getYSEOPConfig;
specImplementation[ChartType.StackedBar].validateAxisConfig =
    specImplementation[ChartType.StackedColumn].validateAxisConfig;
specImplementation[ChartType.StackedBar].configOptions = (chartModel) => {
    const baseConfig = specImplementation[ChartType.StackedColumn].configOptions(chartModel);
    baseConfig.allowedConfigurations.advanced.display.responsiveLayout = true;
    baseConfig.highcharts.yAxis = [
        {
            stackLabels: {
                style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.BAR_CHART, getEnvFlags().applyContrastForDataLabels),
                useHTML: false,
            },
        },
    ];
    baseConfig.allowedConfigurations.transposeXY = true;
    baseConfig.highcharts.xAxis = {
        labels: {
            y: 4,
            style: {
                whiteSpace: 'wrap',
                textOverflow: 'ellipsis',
            },
        },
    };
    return baseConfig;
};
specImplementation[ChartType.StackedBar].shouldShowDetailLabelToggleForColumn = shouldShowDetailLabelToggleForYAxisColumns;
specImplementation[ChartType.StackedBar].shouldShowStackLabelToggleForColumn = shouldShowStackLabelToggleForYAxisColumns;
specImplementation[ChartType.StackedBar].getGridLineConfig =
    specImplementation[ChartType.Bar].getGridLineConfig;
specImplementation[ChartType.StackedArea] = {};
specImplementation[ChartType.StackedArea].validateAxisConfig =
    specImplementation[ChartType.StackedColumn].validateAxisConfig;
specImplementation[ChartType.StackedArea].configOptions = (chartModel) => {
    const baseConfig = specImplementation[ChartType.StackedColumn].configOptions(chartModel);
    baseConfig.highcharts.yAxis = [
        {
            stackLabels: {
                style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.AREA_CHART, getEnvFlags().applyContrastForDataLabels),
                useHTML: false,
            },
        },
    ];
    baseConfig.allowedConfigurations.advanced.display.handleMissingValues = true;
    baseConfig.allowedConfigurations.advanced.display.showNullAsZero = false;
    baseConfig.minPointLengthSupported = false;
    return baseConfig;
};
specImplementation[ChartType.StackedArea].highchartsType = ChartType.Area;
specImplementation[ChartType.StackedArea].getStackedValuesColumn = getDefaultStackingValueColumn;
specImplementation[ChartType.StackedArea].shouldShowDetailLabelToggleForColumn = shouldShowDetailLabelToggleForYAxisColumns;
specImplementation[ChartType.StackedArea].shouldShowStackLabelToggleForColumn = shouldShowStackLabelToggleForYAxisColumns;
specImplementation[ChartType.StackedArea].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.Pareto] = {};
specImplementation[ChartType.Pareto].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!axisConfig.x.length || !axisConfig.y.length || axisConfig.size) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.y.length !== 1 ||
        (axisConfig.color && axisConfig.color.length > 0)) {
        return { isValid: false };
    }
    return { isValid: true };
};
specImplementation[ChartType.Pareto].getYAxisTooltipData = (chartModel, dataModel, pointData, getTranslatedText) => {
    const yAxisColumn = getEffectiveYAxisColumn(chartModel, pointData, getTranslatedText);
    if (pointData.series && pointData.series.userOptions.tooltipFormatter) {
        const yLabel = pointData.series.userOptions.tooltipFormatter(pointData);
        return [
            {
                label: getName(yAxisColumn),
                value: yLabel,
                labelType: TooltipLabelType.Y_LABEL,
            },
        ];
    }
    const value = getDataLabelFromColumn(chartModel, dataModel, yAxisColumn, pointData.y, getTranslatedText);
    const yLabelData = {
        label: getName(yAxisColumn),
        value,
        labelType: TooltipLabelType.Y_LABEL,
    };
    return [yLabelData];
};
specImplementation[ChartType.Pareto].getChartDataValidationError = (chartModel, dataModel) => {
    const yAxisColumns = dataModel.getQueryDefinitions()[0].yAxisColumns;
    if (yAxisColumns.length !== 1) {
        return { type: ErrorTypes.ONLY_ONE_Y_AXIS_COLUMN_SUPPORTED };
    }
    const minYValue = getMinYValue(dataModel, yAxisColumns[0]);
    if (_.isNaN(Number(minYValue)) || minYValue < 0) {
        return { type: ErrorTypes.NON_NEGATIVE_VALUES_NEEDED };
    }
    return null;
};
specImplementation[ChartType.Pareto].configOptions = () => {
    return {
        highcharts: {
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    borderWidth: 0.5,
                    groupPadding: 0.01,
                    pointPadding: 0,
                },
                legend: {
                    enabled: false,
                },
            },
        },
        doNotShowLegend: true,
        enableLegendSelector: false,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                },
            },
            allowSortFormatOption: false,
            zoomPanStateToggle: true,
            showRoundedNumbers: true,
        },
        minPointLengthSupported: true,
        plotsCumulativeMeasure: true,
    };
};
specImplementation[ChartType.Pareto].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Kpi] = {};
specImplementation[ChartType.Kpi].highchartsType = ChartType.Area;
specImplementation[ChartType.Kpi].getChartDataValidationError = (chartModel, dataModel, envOptions) => {
    const series = getSeries(dataModel) || [];
    const isTimeSeries = isKpiChartTypeTimeseries(chartModel);
    if (isTimeSeries) {
        const chartType = getChartType(chartModel);
        const errorType = getKPISeriesXAxisValidationError(dataModel, chartModel, chartType, envOptions);
        if (!errorType) {
            return null;
        }
        return { type: errorType };
    }
    return null;
};
specImplementation[ChartType.Kpi].validateAxisConfig = function (axisConfig, getTranslatedText, chartViz) {
    var _a, _b, _c, _d, _e;
    if (!isKPIChartOnAttributesEnabled() && (!axisConfig.x || !axisConfig.y)) {
        return {
            isValid: false,
        };
    }
    if (axisConfig.y.length !== 1) {
        return {
            isValid: false,
        };
    }
    if (axisConfig.x.length > 1) {
        return {
            isValid: false,
        };
    }
    if (!axisConfig.x.every(_column => validXAxisColumnTimeBuckets.includes(_column === null || _column === void 0 ? void 0 : _column.timeBucket))) {
        return {
            isValid: false,
            errors: {
                x: getTranslatedText('chartEditorErrorExplanation.ONLY_TIME_BUCKET_ON_TIME_AXIS'),
            },
        };
    }
    if (!isKPIChartOnAttributesEnabled() &&
        !isEffectivelyNumeric(axisConfig.y[0])) {
        return {
            isValid: false,
        };
    }
    if (axisConfig.x.length) {
        if (!(isDateColumn(axisConfig.x[0]) || isTimeColumn(axisConfig.x[0]))) {
            return {
                isValid: false,
            };
        }
    }
    if (_.isArray(axisConfig.color) && axisConfig.color.length > 0) {
        return {
            isValid: false,
        };
    }
    let sortedColumnId;
    if ((_a = chartViz === null || chartViz === void 0 ? void 0 : chartViz.topInfo) === null || _a === void 0 ? void 0 : _a.length) {
        sortedColumnId = chartViz === null || chartViz === void 0 ? void 0 : chartViz.topInfo[0].columnId;
    }
    else if ((_b = chartViz === null || chartViz === void 0 ? void 0 : chartViz.sortInfo) === null || _b === void 0 ? void 0 : _b.length) {
        sortedColumnId = (_c = chartViz === null || chartViz === void 0 ? void 0 : chartViz.sortInfo[0]) === null || _c === void 0 ? void 0 : _c.columnId;
    }
    else if ((_d = chartViz === null || chartViz === void 0 ? void 0 : chartViz.sortOrder) === null || _d === void 0 ? void 0 : _d.length) {
        sortedColumnId = (_e = chartViz === null || chartViz === void 0 ? void 0 : chartViz.sortOrder[0]) === null || _e === void 0 ? void 0 : _e.columnId;
    }
    if (sortedColumnId &&
        axisConfig.x.length &&
        axisConfig.x[0].id !== sortedColumnId) {
        return {
            isValid: false,
        };
    }
    return {
        isValid: true,
    };
};
specImplementation[ChartType.Kpi].configOptions = (chartModel) => {
    const customProps = [
        {
            name: 'showLabel',
            type: 'boolean',
            label: 'charts.chartEditor.KPI.SHOW_LABEL',
            constraints: [
                CUSTOM_PROP_CONSTRAINTS.NON_DESCENDING_TIME_COLUMN_ON_X,
            ],
        },
        {
            name: 'showSparkline',
            type: 'boolean',
            label: 'charts.chartEditor.KPI.SHOW_SPARKLINE',
            constraints: [
                CUSTOM_PROP_CONSTRAINTS.NON_DESCENDING_TIME_COLUMN_ON_X,
                CUSTOM_PROP_CONSTRAINTS.NON_ATTRIBUTE_COLUMN_ON_Y,
            ],
        },
        {
            name: 'showComparison',
            type: 'boolean',
            label: 'charts.chartEditor.KPI.SHOW_COMPARISON',
            info: 'charts.chartEditor.KPI.SHOW_COMPARISON_INFO_TEXT',
            constraints: [
                CUSTOM_PROP_CONSTRAINTS.NON_DESCENDING_TIME_COLUMN_ON_X,
                CUSTOM_PROP_CONSTRAINTS.NON_ATTRIBUTE_COLUMN_ON_Y,
                CUSTOM_PROP_CONSTRAINTS.SINGLE_POINT_KPI_ENABLED,
            ],
        },
    ];
    if (isKPIAnomaliesEnabled()) {
        const showAnomaliesSupported = isShowAnomaliesSupported(chartModel);
        customProps.push({
            section: AdvancedSection.CORTEX,
            name: 'showAnomalies',
            type: 'boolean',
            label: 'charts.chartEditor.KPI.SHOW_ANOMALIES',
            info: showAnomaliesSupported
                ? 'charts.chartEditor.KPI.SHOW_ANOMALIES_INFO_TEXT'
                : 'charts.chartEditor.KPI.ANOMALIES_UNSUPPORTED_INFO_TEXT',
            disabled: !showAnomaliesSupported,
            indeterminate: !showAnomaliesSupported,
            reloadOnChange: true,
            constraints: [CUSTOM_PROP_CONSTRAINTS.SHOW_SPARKLINE_ENABLED],
        });
        if (!getEnvFlags().isKPIWithAnomalyV2Enabled) {
            customProps.push({
                section: AdvancedSection.CORTEX,
                name: 'showBounds',
                type: 'boolean',
                label: 'charts.chartEditor.KPI.SHOW_BOUNDS',
                info: 'charts.chartEditor.KPI.SHOW_BOUNDS_INFO_TEXT',
                constraints: [
                    CUSTOM_PROP_CONSTRAINTS.SHOW_SPARKLINE_ENABLED,
                    CUSTOM_PROP_CONSTRAINTS.SHOW_ANOMALIES_ENABLED,
                ],
            });
        }
    }
    if (getEnvFlags().isAnomalyV2Enabled) {
        customProps.push({
            name: 'showAnalyseChangeCta',
            type: 'boolean',
            label: 'charts.chartEditor.KPI.SHOW_ANALYSE_CHANGE_CTA',
            constraints: [
                CUSTOM_PROP_CONSTRAINTS.NON_ATTRIBUTE_COLUMN_ON_Y,
                CUSTOM_PROP_CONSTRAINTS.COMPARISON_ENABLED,
            ],
            hiddenActionFlagsForEmbed: [
                EMBED_HIDDEN_ACTION_FLAGS.KPIAnalysisCTA,
                EMBED_HIDDEN_ACTION_FLAGS.EnableContextualChangeAnalysis,
            ],
        });
    }
    const xAxisColumn = _.first(getXAxisColumns(chartModel));
    if (!isChartAggregatedValOnKPIEnabled() &&
        isCustomComparisonOnChartEnabled() &&
        xAxisColumn) {
        customProps.push({
            name: 'customCompare',
            type: 'select',
            label: 'charts.chartEditor.KPI.CUSTOM_COMPARISON',
            constraints: [CUSTOM_PROP_CONSTRAINTS.COMPARISON_ENABLED],
            getOptions: (chartModelWithData) => getCustomComparisonOptions(chartModelWithData),
            getSelectedOptions: (chartModelWithData, currentValue, defaultValue, updateCustomProps) => {
                if (_.isNil(chartModelWithData) ||
                    _.isUndefined(currentValue)) {
                    return [defaultValue];
                }
                const isSelectedOptionInOptions = getCustomComparisonOptions(chartModelWithData).some(optionGroup => optionGroup.options.some(option => option.id === currentValue));
                if (!isSelectedOptionInOptions) {
                    updateCustomProps(defaultValue, 'customCompare', false);
                    return [defaultValue];
                }
                return [currentValue];
            },
            onSelectError: (selectedOption, chartModelWithData) => {
                const refTuple = _.find(getRefTuple(chartModelWithData, selectedOption.id));
                if (!refTuple) {
                    return {
                        type: 'COMPARISON_DATE_OUT_OF_RANGE',
                        data: selectedOption,
                    };
                }
                return null;
            },
        });
        const currentRefTuple = getEffectiveRefTuple(chartModel);
        if (isGenericCustomCompareEnabled() &&
            currentRefTuple &&
            currentRefTuple.type === TupleType.Custom) {
            const currentCustomProps = getChartSpecificCustomProps(chartModel) || {};
            const genericCompareValue = currentCustomProps.genericCompareValue;
            const customCompareLabel = mapTimeBucketToCategory(getTimeBucket(xAxisColumn), genericCompareValue === '1');
            if (!_.isUndefined(customCompareLabel)) {
                customProps.push({
                    name: 'genericCustomCompare',
                    type: 'string',
                    subLabel: translate('charts.chartEditor.KPI.CUSTOM_COMPARISON_LABEL', {
                        labelstring: customCompareLabel,
                    }).toLowerCase(),
                    label: translate('charts.chartEditor.KPI.GENERIC_CUSTOM_COMPARISON'),
                    constraints: [CUSTOM_PROP_CONSTRAINTS.COMPARISON_ENABLED],
                    defaultValue: _.isUndefined(currentCustomProps.genericCompareValue)
                        ? '1'
                        : currentCustomProps.genericCompareValue,
                });
            }
        }
    }
    const margins = {};
    if (getEnvFlags().isKPIWithAnomalyV2Enabled &&
        !!chartModel &&
        !(chartModel === null || chartModel === void 0 ? void 0 : chartModel.hideAnalyzeButton)) {
        margins.marginBottom = ANOMALY_BAR_HEIGHT;
    }
    const spacing = {
        spacingBottom: 4,
        spacingTop: 4,
        spacingLeft: 0,
    };
    if (getEnvFlags().isKPIWithAnomalyV2Enabled) {
        spacing.spacingRight = 8;
    }
    return {
        shouldBringSelectedSeriesToFront: false,
        noMeasuresOnXAxis: true,
        attributesOnYAxis: isKPIChartOnAttributesEnabled(),
        incompleteZone: incompleteZoneConfigTemplate1,
        allowedConfigurations: {
            allowColorChange: isKpiChartTypeTimeseries(chartModel),
            conditionalFormatEditorParams: {
                isPlotAsBand: false,
                isFont: true,
                isBackgroundColorPickerAvailable: false,
                isConditionalFormattingAllowedOnAttributeColumn: true,
            },
            advanced: {
                display: {
                    showAllLabels: false,
                    gridLines: false,
                    showMaxDataPoints: isKpiChartTypeTimeseries(chartModel),
                    handleMissingValues: true,
                    chartSpecific: {
                        customProps,
                    },
                    tooltipCustomization: isKpiChartTypeTimeseries(chartModel),
                },
            },
            excludeNullValues: true,
            useCustomColorSelector: !!chartModel && isAllowedCustomColorPallete(chartModel),
            showDataLabels: false,
            conditionalFormatting: true,
            optionalXAxisColumns: true,
            onlyTimeSeriesOnXAxis: true,
            allowAxisCustomization: false,
            allowMultipleColumnsOnYAxis: false,
            allowChartCustomisation: false,
            allowSortFormatOption: false,
        },
        enableLegendSelector: false,
        doNotShowLegend: true,
        showHighchartTitle: false,
        highcharts: {
            xAxis: {
                visible: getEnvFlags().isKPIWithAnomalyV2Enabled,
                startOnTick: false,
                endOnTick: false,
                tickPositions: [],
                ...(getEnvFlags().isKPIWithAnomalyV2Enabled && {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        enabled: false,
                    },
                }),
            },
            yAxis: {
                visible: false,
                startOnTick: false,
                endOnTick: false,
                tickPositions: [0],
                ...(getEnvFlags().isKPIWithAnomalyV2Enabled && {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        enabled: false,
                    },
                }),
            },
            chart: {
                ...spacing,
                ...margins,
                events: {
                    load() {
                        kpiChartEvents.onLoad(this, chartModel, chartModel === null || chartModel === void 0 ? void 0 : chartModel.hideAnalyzeButton);
                    },
                    beforeRender() {
                        kpiChartEvents.onBeforeRender(this, chartModel);
                    },
                },
            },
            plotOptions: {
                area: {
                    fillOpacity: 0.2,
                    allowPointSelect: true,
                    point: {
                        events: {
                            unselect() {
                                kpiChartEvents.onUnselectPoint(this, chartModel);
                            },
                            select() {
                                const isAnomaly = isPointAnomaly(chartModel, this.options.xRawValues[0]);
                                this.update({
                                    marker: {
                                        enabled: true,
                                    },
                                    ...(isAnomaly &&
                                        !getEnvFlags()
                                            .isKPIWithAnomalyV2Enabled && {
                                        color: getAnomalyColor(chartModel),
                                    }),
                                });
                            },
                            mouseOver() {
                                if (getEnvFlags().isKPIWithAnomalyV2Enabled) {
                                    const chart = this.series.chart;
                                    const yAxis = chart.yAxis[0];
                                    let lineEndY = chart.plotTop + chart.plotHeight;
                                    if (yAxis.plotLinesAndBands &&
                                        yAxis.plotLinesAndBands.length) {
                                        const plotBand = yAxis.plotLinesAndBands[0];
                                        const toY = yAxis.toPixels(plotBand.options.to, true);
                                        lineEndY = Math.min(chart.plotTop + chart.plotHeight, toY + 5);
                                    }
                                    if (chart.customCrosshair) {
                                        chart.customCrosshair.show();
                                        chart.customCrosshair.attr({
                                            d: [
                                                'M',
                                                this.plotX + chart.plotLeft,
                                                this.plotY + chart.plotTop,
                                                'L',
                                                this.plotX + chart.plotLeft,
                                                lineEndY,
                                            ],
                                            stroke: this.color || this.series.color,
                                        });
                                    }
                                }
                            },
                            mouseOut() {
                                if (getEnvFlags().isKPIWithAnomalyV2Enabled) {
                                    const chart = this.series.chart;
                                    if (chart.customCrosshair) {
                                        chart.customCrosshair.hide();
                                    }
                                }
                            },
                        },
                    },
                    pointPlacement: 'on',
                },
            },
        },
        axesAlias: {
            xAxis: 'chart.chartEditor.chartAxisConfigurator.TIME_AXIS',
            yAxis: 'chart.chartEditor.chartAxisConfigurator.VISUALIZED',
        },
    };
};
specImplementation[ChartType.Kpi].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Kpi].customColorSelectorConfig = (chartModel, onColorSelect, getTranslatedText) => {
    const colorSelectors = [];
    if (!areAllTimeSeriesColumns(getXAxisColumns(chartModel))) {
        return colorSelectors;
    }
    const customColors = getCustomColorsSelectorArray(chartModel);
    if (isComparisionEnabled(chartModel)) {
        const labelForPositiveColor = getTranslatedText('charts.chartEditor.KPI.POSITIVE_CHANGE_COLOR');
        const labelForNegativeColor = getTranslatedText('charts.chartEditor.KPI.NEGATIVE_CHANGE_COLOR');
        const positiveColorSelectOption = {
            label: labelForPositiveColor,
            color: customColors[positiveComparisionColorIndex],
            closeCallback: color => {
                onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], Object.assign([], getCustomColorsSelectorArray(chartModel), { [positiveComparisionColorIndex]: color }));
            },
        };
        const negativeColorSelectOption = {
            label: labelForNegativeColor,
            color: customColors[negativeComparisionColorIndex],
            closeCallback: color => {
                onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], Object.assign([], getCustomColorsSelectorArray(chartModel), { [negativeComparisionColorIndex]: color }));
            },
        };
        colorSelectors.push(positiveColorSelectOption, negativeColorSelectOption);
    }
    if (isShowAnomaliesSupported(chartModel) &&
        isShowAnomaliesEnabled(chartModel)) {
        if (!getEnvFlags().isKPIWithAnomalyV2Enabled) {
            const labelForAnomalyColor = getTranslatedText('charts.chartEditor.KPI.ANOMALY_COLOR');
            const anomalyColorSelectOption = {
                section: AdvancedSection.CORTEX,
                label: labelForAnomalyColor,
                color: customColors[anomalyColorIndex],
                closeCallback: color => {
                    onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], Object.assign([], getCustomColorsSelectorArray(chartModel), { [anomalyColorIndex]: color }));
                },
            };
            colorSelectors.push(anomalyColorSelectOption);
            if (isShowBoundsEnabled(chartModel)) {
                const labelForBoundsColor = getTranslatedText('charts.chartEditor.KPI.BOUNDS_COLOR');
                const boundsColorSelectOption = {
                    section: AdvancedSection.CORTEX,
                    label: labelForBoundsColor,
                    color: customColors[boundColorIndex],
                    closeCallback: color => {
                        onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], Object.assign([], getCustomColorsSelectorArray(chartModel), { [boundColorIndex]: color }));
                    },
                };
                colorSelectors.push(boundsColorSelectOption);
            }
        }
        else {
            const labelForAboveExpectedColor = getTranslatedText('ABOVE_EXPECTED_ANOMALY');
            const aboveExpectedColorSelectOption = {
                section: AdvancedSection.CORTEX,
                label: labelForAboveExpectedColor,
                color: customColors[ABOVE_EXPECTED_COLOR_INDEX],
                closeCallback: color => {
                    onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], Object.assign([], getCustomColorsSelectorArray(chartModel), { [ABOVE_EXPECTED_COLOR_INDEX]: color }));
                },
            };
            colorSelectors.push(aboveExpectedColorSelectOption);
            const labelForBelowExpectedColor = getTranslatedText('BELOW_EXPECTED_ANOMALY');
            const belowExpectedColorSelectOption = {
                section: AdvancedSection.CORTEX,
                label: labelForBelowExpectedColor,
                color: customColors[BELOW_EXPECTED_COLOR_INDEX],
                closeCallback: color => {
                    onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], Object.assign([], getCustomColorsSelectorArray(chartModel), { [BELOW_EXPECTED_COLOR_INDEX]: color }));
                },
            };
            colorSelectors.push(belowExpectedColorSelectOption);
        }
    }
    return colorSelectors;
};
specImplementation[ChartType.Kpi].seriesOverride =
    seriesOverrides[ChartType.Kpi];
specImplementation[ChartType.Line] = {};
specImplementation[ChartType.Line].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length && !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Line].configOptions = (chartModel) => {
    return {
        highcharts: {
            xAxis: {
                lineColor: 'var(--ts-var-chart-x-axis-line-color, #e0e0e0)',
                lineWidth: 2,
            },
            yAxis: {
                lineColor: 'var(--ts-var-chart-y-axis-line-color, #e0e0e0)',
                lineWidth: 2,
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: !!chartModel && isMarkersEnabled(chartModel),
                    },
                    allowPointSelect: true,
                    point: {
                        events: {
                            unselect() {
                                this.update({
                                    marker: {},
                                });
                            },
                            select() {
                                this.update({
                                    marker: {
                                        enabled: true,
                                    },
                                });
                            },
                        },
                    },
                },
            },
        },
        useRainbowColors: true,
        useDefaultMinTick: true,
        incompleteZone: incompleteZoneConfigTemplate1,
        hasStackedPointsForMultiSeries: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    markersEnabled: true,
                    regressionLine: true,
                    responsiveLayout: true,
                    handleMissingValues: true,
                },
            },
            excludeNullValues: true,
            zoomPanStateToggle: true,
            yAxisRange: true,
            regressionLine: true,
            markersEnabled: true,
            showRoundedNumbers: true,
        },
    };
};
specImplementation[ChartType.Line].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Line].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.Area] = {};
specImplementation[ChartType.Area].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length && !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Area].configOptions = () => {
    return {
        incompleteZone: incompleteZoneConfigTemplate1,
        allowedConfigurations: {
            zoomPanStateToggle: true,
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    responsiveLayout: true,
                    handleMissingValues: true,
                },
            },
            excludeNullValues: true,
            yAxisRange: true,
            showRoundedNumbers: true,
        },
        highcharts: {
            plotOptions: {
                area: {
                    allowPointSelect: true,
                },
            },
        },
    };
};
specImplementation[ChartType.Area].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Area].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.Scatter] = {};
specImplementation[ChartType.Scatter].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    const colorColumns = axisConfig.color || [];
    let categoryColumn = null;
    if (axisConfig.category && axisConfig.category.length === 1) {
        categoryColumn = _.first(axisConfig.category);
    }
    if (axisConfig.x.length &&
        axisConfig.y.length &&
        (!areAllEffectivelyNumeric([...axisConfig.x, ...axisConfig.y]) ||
            colorColumns.length ||
            categoryColumn) &&
        !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Scatter].configOptions = () => {
    return {
        noMeasuresOnXAxis: false,
        highcharts: {
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    marker: {
                        enabled: true,
                    },
                },
            },
        },
        highThresholdGrouping: true,
        attributesOnYAxis: true,
        categoryAxisEnabled: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    regressionLine: true,
                },
            },
            zoomPanStateToggle: true,
            xAxisRange: true,
            yAxisRange: true,
            regressionLine: true,
            showRoundedNumbers: true,
        },
    };
};
specImplementation[ChartType.Scatter].shouldShowDataLabelToggleForColumn = (chartModel, columnId) => {
    if (!!_.first(getCategoryColumns(chartModel)) &&
        getId(_.first(getCategoryColumns(chartModel))) === columnId) {
        return true;
    }
    return isYAxisColumn(chartModel, columnId);
};
specImplementation[ChartType.Scatter].getGridLineConfig = disableDefaultGridlines;
specImplementation[ChartType.Bubble] = {};
specImplementation[ChartType.Bubble].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    const colorColumns = axisConfig.color || [];
    let categoryColumn = null;
    if (axisConfig.category && axisConfig.category.length === 1) {
        categoryColumn = _.first(axisConfig.category);
    }
    if (axisConfig.x.length &&
        axisConfig.y.length &&
        (!areAllEffectivelyNumeric([...axisConfig.x, ...axisConfig.y]) ||
            colorColumns.length ||
            categoryColumn) &&
        axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Bubble].configOptions = () => {
    return {
        noMeasuresOnXAxis: false,
        highcharts: {
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    marker: {
                        enabled: true,
                    },
                },
            },
        },
        radialEnabled: true,
        categoryAxisEnabled: true,
        useAlphaColors: true,
        useRainbowColors: true,
        attributesOnYAxis: true,
        highThresholdGrouping: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                    regressionLine: true,
                },
            },
            zoomPanStateToggle: true,
            xAxisRange: true,
            yAxisRange: true,
            regressionLine: true,
            showRoundedNumbers: true,
        },
    };
};
specImplementation[ChartType.Bubble].seriesOverride =
    seriesOverrides[ChartType.Bubble];
specImplementation[ChartType.Bubble].highchartsType = ChartType.Scatter;
specImplementation[ChartType.Bubble].shouldShowDataLabelToggleForColumn =
    specImplementation[ChartType.Scatter].shouldShowDataLabelToggleForColumn;
specImplementation[ChartType.Bubble].getGridLineConfig =
    specImplementation[ChartType.Scatter].getGridLineConfig;
specImplementation[ChartType.Treemap] = {};
specImplementation[ChartType.Treemap].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length === 1 && axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Treemap].seriesOverride =
    seriesOverrides[ChartType.Treemap];
specImplementation[ChartType.Treemap].getChartDataValidationError = (chartModel, dataModel) => {
    const data = getDefaultQueryData(dataModel);
    if (data.length > chartSpecificConstants.TREEMAP_CARDINALITY_THRESHOLD) {
        return { type: ErrorTypes.TREEMAP_CARDINALITY_ERROR };
    }
    const sizeColumn = dataModel.getQueryDefinitions()[0].radialColumn;
    if (containsAllNegativeValuesOnSizeColumn(sizeColumn, data)) {
        return { type: ErrorTypes.VIZ_BLANK_FOR_ALL_NEGATIVE_VALUES };
    }
    return null;
};
specImplementation[ChartType.Treemap].configOptions = (chartModel) => {
    return {
        colorAxisColumn: chartModel && _.first(getYAxisColumns(chartModel)),
        highcharts: {
            chart: {
                plotBorderWidth: 0,
                spacingTop: 10,
            },
            legend: {
                symbolWidth: 300,
                verticalAlign: 'bottom',
                layout: 'horizontal',
                align: 'center',
                autoRotation: [-45],
                navigation: {
                    enabled: false,
                },
                title: {
                    text: (chartModel && getYAxisColumns(chartModel).length > 0) &&
                        getName(_.first(getYAxisColumns(chartModel))),
                    style: {
                        color: 'var(--ts-var-chart-treemap-legend-title-color, var(--rd-sys-color-content-primary))',
                    },
                },
            },
            colorAxis: {
                labels: {
                    style: {
                        fontFamily: 'Optimo-Plain, Helvetica Neue, Helvetica, Arial, sans-serif',
                        color: 'var(--ts-var-chart-treemap-legend-label-color, var(--rd-sys-color-content-primary))',
                    },
                },
            },
            plotOptions: {
                treemap: {
                    layoutAlgorithm: 'squarified',
                    dataLabels: {
                        style: {
                            fontFamily: 'Optimo-Plain,' +
                                ' Helvetica Neue, Helvetica, Arial, sans-serif',
                            fontSize: '12px',
                            color: CustomStylingConfig.getDefaultChartFontColor(),
                            textOutline: 'none',
                            ...(getEnvFlags().applyContrastForDataLabels &&
                                CONTRAST_SETTINGS),
                        },
                    },
                },
                series: {
                    dataGrouping: {
                        enabled: false,
                    },
                    borderWidth: 1,
                },
            },
        },
        radialEnabled: true,
        enableLegendSelector: false,
        attributesOnYAxis: false,
        axesAlias: {
            xAxis: 'chart.chartEditor.chartAxisConfigurator.CATEGORY',
            yAxis: 'chart.chartEditor.chartAxisConfigurator.COLOR_AXIS',
            radial: 'chart.chartEditor.chartAxisConfigurator.RADIAL',
        },
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                    showAllLabels: false,
                },
            },
            allowAxisCustomization: false,
            gridLines: false,
            showAxes: false,
            showRoundedNumbers: true,
            showColorAxis: true,
            showDataLabels: false,
            allowColorChange: true,
            conditionalFormatting: isAllowGradientColoring(),
            gradientEnabled: isAllowGradientColoring(),
            allowSortFormatOption: false,
        },
        dataLabelsAlwaysEnabled: true,
        dataLabelsEnabledByDefault: true,
        doNotShowLegend: true,
        incompleteZone: {
            disabled: true,
        },
    };
};
specImplementation[ChartType.Heatmap] = {};
specImplementation[ChartType.Heatmap].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y || !axisConfig.size) {
        return { isValid: false };
    }
    if (axisConfig.x.length !== 1) {
        return { isValid: false };
    }
    if (axisConfig.y.length !== 1) {
        return { isValid: false };
    }
    if (isEffectivelyNumeric(axisConfig.y[0])) {
        return { isValid: false };
    }
    return { isValid: true };
};
specImplementation[ChartType.Heatmap].seriesOverride =
    seriesOverrides[ChartType.Heatmap];
specImplementation[ChartType.Heatmap].configOptions = (chartModel) => {
    const radialColumn = chartModel && getRadialColumn(chartModel);
    const config = {
        colorAxisColumn: radialColumn,
        highcharts: {
            chart: {
                plotBorderWidth: 0,
                spacingTop: 10,
            },
            yAxis: [
                {
                    type: 'category',
                    endOnTick: false,
                },
            ],
            colorAxis: {
                labels: {
                    style: {
                        fontFamily: 'Optimo-Plain, Helvetica Neue, Helvetica, Arial, sans-serif',
                        color: 'var(--ts-var-chart-heatmap-legend-label-color, var(--rd-sys-color-content-primary))',
                    },
                },
                endOnTick: true,
            },
            legend: {
                symbolWidth: 300,
                autoRotation: [-45],
                verticalAlign: 'bottom',
                layout: 'horizontal',
                align: 'center',
                title: {
                    text: radialColumn && getName(radialColumn),
                    style: {
                        color: 'var(--ts-var-chart-heatmap-legend-title-color, var(--rd-sys-color-content-primary))',
                    },
                },
            },
            plotOptions: {
                series: {
                    borderWidth: 0,
                    dataGrouping: {
                        enabled: false,
                    },
                    marker: {
                        enabled: true,
                        symbol: 'rect',
                        lineWidth: 0,
                    },
                    dataLabels: {
                        enabled: false,
                        style: {
                            fontFamily: 'Optimo-Plain',
                            fontSize: '12px',
                            color: 'var(--ts-var-axis-data-label-color,#333333)',
                            textOutline: 'none',
                            ...(getEnvFlags().applyContrastForDataLabels &&
                                CONTRAST_SETTINGS),
                        },
                    },
                },
                candlestick: {
                    lineColor: '#e36028',
                },
                map: {
                    shadow: false,
                },
            },
        },
        radialEnabled: true,
        enableLegendSelector: false,
        attributesOnYAxis: true,
        noMeasuresOnYAxis: true,
        axesAlias: {
            radial: 'Value',
        },
        dataLabelsEnabledByDefault: true,
        doNotShowLegend: true,
        allowedConfigurations: {
            conditionalFormatEditorParams: {
                isPlotAsBand: false,
                isFont: true,
                isBackgroundColorPickerAvailable: true,
                isConditionalFormattingAllowedOnRadialColumn: true,
            },
            advanced: {
                display: {
                    gridLines: false,
                    showAllLabels: true,
                },
            },
            showAxes: false,
            gridLines: false,
            showRoundedNumbers: true,
            showColorAxis: true,
            showDataLabels: true,
            conditionalFormatting: isAllowGradientColoring(),
            gradientEnabled: isAllowGradientColoring(),
            configureColorForRadialAttrs: true,
            configureColorForYAttrs: false,
        },
        dataLabelsAlwaysEnabled: true,
        incompleteZone: {
            disabled: true,
        },
    };
    return config;
};
specImplementation[ChartType.Heatmap].shouldShowDataLabelToggleForColumn = (chartModel, columnId) => {
    return !!(getRadialColumn(chartModel) &&
        getId(getRadialColumn(chartModel)) === columnId);
};
specImplementation[ChartType.Waterfall] = {};
specImplementation[ChartType.Waterfall].validateAxisConfig =
    specImplementation[ChartType.Column].validateAxisConfig;
specImplementation[ChartType.Waterfall].configOptions = () => {
    return {
        highcharts: {
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    dataLabels: {
                        inside: false,
                        style: {
                            fontFamily: 'var(--ts-var-axis-data-label-font-family, Optimo-Plain)',
                            fontSize: '12px',
                        },
                    },
                    dashStyle: 'ShortDash',
                    borderWidth: 0,
                },
            },
            colors: ChartThemeService.getDefaultTheme().primaryColors,
        },
        useRainbowColors: true,
        dataLabelsEnabledByDefault: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    zoomPanStateToggle: true,
                },
            },
            zoomPanStateToggle: true,
            showRoundedNumbers: true,
        },
        minPointLengthSupported: true,
        incompleteZone: {
            disabled: true,
        },
    };
};
specImplementation[ChartType.Waterfall].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Waterfall].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.Pie] = {};
specImplementation[ChartType.Pie].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (axisConfig.x.length < 1) {
        return { isValid: false };
    }
    if (_.isArray(axisConfig.color) && axisConfig.color.length > 0) {
        return { isValid: false };
    }
    if (axisConfig.y.length > 2 || axisConfig.y.length < 1) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y) ||
        !areAllAttributes(axisConfig.x)) {
        return { isValid: false };
    }
    if (containsGrowthColumn(axisConfig.y)) {
        return { isValid: false };
    }
    return { isValid: !axisConfig.size };
};
specImplementation[ChartType.Pie].seriesOverride =
    seriesOverrides[ChartType.Pie];
specImplementation[ChartType.Pie].getLegendCardinalityError = (chartModel, dataModel) => {
    return !testXAxisLegendValid(chartModel, dataModel);
};
specImplementation[ChartType.Pie].getChartDataValidationError = (chartModel, dataModel) => {
    const data = getDefaultQueryData(dataModel);
    const yAxisColumns = dataModel.getQueryDefinitions()[0].yAxisColumns;
    if (containsNegativeValuesOnY(yAxisColumns, data)) {
        return { type: ErrorTypes.NON_NEGATIVE_VALUES_NEEDED };
    }
    if (containsAllZeroValuesOnY(yAxisColumns, data)) {
        return { type: ErrorTypes.PIE_NO_POSITIVE_VALUES };
    }
    return null;
};
specImplementation[ChartType.Pie].configOptions = () => {
    return {
        isMultiColorSeries: true,
        doNotShowLegend: true,
        enableLegendSelector: false,
        dataLabelsAlwaysEnabled: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                },
            },
            allowAxisCustomization: false,
            showAxes: false,
            gridLines: false,
            showRoundedNumbers: true,
            showDataLabels: true,
            allowColorChange: true,
            configureColorForXAttrs: true,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: false,
        },
        axesAlias: {
            xAxis: 'chart.chartEditor.chartAxisConfigurator.CATEGORY',
            yAxis: 'chart.chartEditor.chartAxisConfigurator.SIZE',
        },
        highcharts: {
            chart: {
                spacingBottom: 20,
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    animation: true,
                    center: ['50%', '50%'],
                    groupPadding: 0.01,
                    pointPadding: 0,
                    point: {
                        events: {
                            ...(isPieChartEnhancementsEnabled() &&
                                !getEnvFlags()
                                    .isFlipTooltipToContextMenuEnabled && {
                                mouseOver() {
                                    pointSelectionUtil.setPointHoverState(this);
                                },
                            }),
                        },
                    },
                },
                series: {
                    dataLabels: {
                        distance: 5,
                    },
                    dataGrouping: {
                        enabled: false,
                    },
                },
            },
            allowedConfigurations: {
                advanced: {
                    display: {
                        gridLines: false,
                    },
                },
                gridLines: false,
            },
        },
    };
};
specImplementation[ChartType.Pie].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.LineColumn] = {};
specImplementation[ChartType.LineColumn].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length > 1) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.LineColumn].seriesOverride =
    seriesOverrides[ChartType.LineColumn];
specImplementation[ChartType.LineColumn].configOptions = (chartModel) => {
    const hasMultipleSeries = chartModel && getYAxisColumns(chartModel).length > 2;
    const groupPadding = hasMultipleSeries ? 0.02 : 0;
    const borderWidth = hasMultipleSeries ? 0 : 0.5;
    return {
        hasStackedPointsForMultiSeries: true,
        shouldBringSelectedSeriesToFront: false,
        minPointLengthSupported: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    responsiveLayout: true,
                    zoomPanStateToggle: true,
                    handleMissingValues: true,
                    showNullAsZero: true,
                },
            },
            excludeNullValues: true,
            zoomPanStateToggle: true,
            yAxisRange: true,
            showRoundedNumbers: true,
        },
        highcharts: {
            plotOptions: {
                series: {
                    borderWidth,
                    pointPadding: 0.1,
                    groupPadding,
                    allowPointSelect: true,
                },
                column: {
                    allowPointSelect: true,
                },
            },
        },
    };
};
specImplementation[ChartType.LineColumn].highchartsType = ChartType.Column;
specImplementation[ChartType.LineColumn].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.LineColumn].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.Funnel] = {};
specImplementation[ChartType.Funnel].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (axisConfig.x.length < 1) {
        return { isValid: false };
    }
    if (_.isArray(axisConfig.color) && axisConfig.color.length > 0) {
        return { isValid: false };
    }
    if (axisConfig.y.length !== 1) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (containsGrowthColumn(axisConfig.y)) {
        return { isValid: false };
    }
    return { isValid: !axisConfig.size };
};
specImplementation[ChartType.Funnel].getLegendCardinalityError = (chartModel, dataModel) => {
    return !testXAxisLegendValid(chartModel, dataModel);
};
specImplementation[ChartType.Funnel].getChartDataValidationError = (chartModel, dataModel) => {
    const data = getDefaultQueryData(dataModel);
    const yAxisColumns = dataModel.getQueryDefinitions()[0].yAxisColumns;
    if (containsNegativeValuesOnY(yAxisColumns, data)) {
        return { type: ErrorTypes.NON_NEGATIVE_VALUES_NEEDED };
    }
    return null;
};
specImplementation[ChartType.Funnel].configOptions = () => {
    return {
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                },
            },
            allowAxisCustomization: false,
            showAxes: false,
            gridLines: false,
            showRoundedNumbers: true,
            showDataLabels: true,
            allowColorChange: true,
            configureColorForXAttrs: true,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: false,
        },
        doNotShowLegend: true,
        enableLegendSelector: false,
        isMultiColorSeries: true,
        dataLabelsAlwaysEnabled: true,
        axesAlias: {
            xAxis: 'chart.chartEditor.chartAxisConfigurator.CATEGORY',
            yAxis: 'chart.chartEditor.chartAxisConfigurator.SIZE',
        },
        highcharts: {
            chart: {
                spacingBottom: 30,
                spacingTop: 30,
            },
            plotOptions: {
                funnel: {
                    neckHeight: '0%',
                    center: ['35%', '50%'],
                    width: '70%',
                },
                series: {
                    allowPointSelect: true,
                    dataLabels: {
                        distance: 5,
                        style: {
                            fontWeight: 'normal',
                            fontFamily: 'var(--ts-var-axis-data-label-font-family, Optimo-Plain)',
                            color: 'var(--ts-var-axis-data-label-color,#5e5e5e)',
                        },
                    },
                    dataGrouping: {
                        enabled: false,
                    },
                },
            },
        },
    };
};
specImplementation[ChartType.Funnel].getYSEOPConfig =
    specImplementation[ChartType.Pie].getYSEOPConfig;
specImplementation[ChartType.Funnel].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.Sankey] = {};
specImplementation[ChartType.Sankey].getChartDataValidationError = (chartModel, dataModel) => {
    if (!dataModel.nodeByColumn) {
        return null;
    }
    const isValid = dataModel.maxNodesInColumns <
        chartSpecificConstants.SANKEY_CHART_CARDINALITY_THRESHOLD;
    return isValid ? null : { type: ErrorTypes.SANKEY_CARDINALITY_ERROR };
};
specImplementation[ChartType.Sankey].configOptions = () => {
    return {
        doNotShowLegend: true,
        enableLegendSelector: false,
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                    showAllLabels: false,
                },
            },
            allowAxisCustomization: false,
            showDataLabels: false,
            gridLines: false,
            allowColorChange: false,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: false,
            allowChartCustomisation: false,
            allowSortFormatOption: false,
        },
    };
};
function getExtraQueryForNetworkModel(sourceNodeColumnJson, targetNodeColumnJson, valueColumnJson, hiddenColumns) {
    return {
        categories: {
            columns: [sourceNodeColumnJson, targetNodeColumnJson],
        },
        values: {
            columns: [valueColumnJson],
        },
        hiddenColumns,
    };
}
specImplementation[ChartType.Sankey].getExtraQueries = function (axisConfig, chartModel) {
    const measureColumn = axisConfig.y[0];
    return axisConfig.x.slice(1, axisConfig.x.length - 1).map((column, index) => getExtraQueryForNetworkModel(column, axisConfig.x[index + 2], measureColumn, null));
};
specImplementation[ChartType.Sankey].getQueryDefinitions = function (axisConfig) {
    const measureColumn = axisConfig.y[0];
    return axisConfig.x
        .slice(0, axisConfig.x.length - 1)
        .map((column, index) => ({
        x: [column, axisConfig.x[index + 1]],
        y: [measureColumn],
    }));
};
specImplementation[ChartType.Sankey].validateAxisConfig = function (axisConfig) {
    var _a;
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (!!axisConfig.size || ((_a = axisConfig.color) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        return { isValid: false };
    }
    if (axisConfig.x.length > 1 && axisConfig.y.length === 1) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.WhiskerScatter] = {};
specImplementation[ChartType.WhiskerScatter].highchartsType = 'boxplot';
specImplementation[ChartType.WhiskerScatter].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length === 1 && axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.WhiskerScatter].seriesOverride =
    seriesOverrides[ChartType.WhiskerScatter];
specImplementation[ChartType.WhiskerScatter].configOptions = () => {
    return {
        allowedConfigurations: {
            advanced: {
                display: {
                    responsiveLayout: true,
                },
            },
            showRoundedNumbers: true,
            yAxisRange: true,
        },
        highcharts: {
            chart: {
                plotBorderWidth: 0,
            },
            plotOptions: {
                boxplot: {
                    lineWidth: 2,
                    medianWidth: 8,
                    pointWidth: 8,
                    stemWidth: 2,
                    whiskerLength: '150%',
                },
            },
        },
        radialEnabled: true,
        attributesOnYAxis: false,
        axesAlias: {
            radial: 'Variance',
        },
    };
};
specImplementation[ChartType.SpiderWeb] = {};
specImplementation[ChartType.SpiderWeb].highchartsType = ChartType.SpiderWeb;
specImplementation[ChartType.SpiderWeb].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (containsGrowthColumn(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.y.length > 1) {
        return { isValid: false };
    }
    return { isValid: !axisConfig.size };
};
specImplementation[ChartType.SpiderWeb].getChartDataValidationError = (chartModel, dataModel) => {
    const isValid = getSeries(dataModel).some(s => s.data.length >=
        chartSpecificConstants.SPIDER_LOW_CARDINALITY_THRESHOLD);
    return isValid ? null : { type: ErrorTypes.SPIDER_WEB_CARDINALITY_ERROR };
};
specImplementation[ChartType.SpiderWeb].configOptions = () => {
    return {
        cardinalityIndexStartsFromZero: true,
        getMinTickIntervalForXAxis(axisCardinality) {
            if (axisCardinality >
                chartSpecificConstants.SPIDER_HIGH_CARDINALITY_THRESHOLD) {
                return axisCardinality / chartSpecificConstants.SPIDER_SLICES;
            }
        },
        axesAlias: {
            xAxis: 'chart.chartEditor.chartAxisConfigurator.DIMENSION',
            yAxis: 'chart.chartEditor.chartAxisConfigurator.MEASURES',
        },
        highcharts: {
            chart: {
                polar: true,
                type: 'line',
                spacingBottom: 50,
                spacingLeft: 50,
                spacingRight: 50,
            },
            pane: {
                startAngle: 180,
                size: '80%',
            },
            xAxis: {
                tickmarkPlacement: 'on',
                lineWidth: 0,
                gridLineColor: chartSpecificConstants.SPIDER_X_GRID_COLOR,
                title: null,
                labels: {
                    distance: chartSpecificConstants.SPIDER_LABEL_DISTANCE,
                    align: null,
                    y: null,
                },
            },
            yAxis: [
                {
                    gridLineInterpolation: 'polygon',
                    tickmarkPlacement: 'on',
                    lineWidth: 0,
                    title: null,
                    gridLineColor: chartSpecificConstants.SPIDER_Y_GRID_COLOR,
                },
            ],
        },
        allowedConfigurations: {
            gridLineConfig: {
                xGridlineEnabled: true,
                yGridlineEnabled: true,
            },
            advanced: {
                display: {
                    gridLines: true,
                },
            },
            allowAxisCustomization: false,
            showRoundedNumbers: true,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: false,
            allowSortFormatOption: false,
        },
    };
};
specImplementation[ChartType.SpiderWeb].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.SpiderWeb].getGridLineConfig = function (chartType, axisConfigs) {
    return {
        xGridlineEnabled: true,
        yGridlineEnabled: true,
    };
};
specImplementation[ChartType.LineStackedColumn] = {};
specImplementation[ChartType.LineStackedColumn].getLegendLabels = (chartModel, dataModel) => {
    if (getLegendColumns(chartModel).length > 0) {
        return getLegendLabelsFromYAxisColumns(getYAxisColumns(chartModel).slice(1)).concat(getLegendLabelsFromLegendColumns(dataModel, chartModel));
    }
    return getLegendLabelsFromYAxisColumns(getYAxisColumns(chartModel));
};
specImplementation[ChartType.LineStackedColumn].getQueryDefinitions = function (axisConfig, chartModel) {
    const queryDefs = [];
    queryDefs.push({
        x: axisConfig.x,
        y: axisConfig.y.slice(0, 1),
        color: axisConfig.color,
    });
    queryDefs.push({
        x: axisConfig.x,
        y: axisConfig.y.slice(1),
    });
    return queryDefs;
};
specImplementation[ChartType.LineStackedColumn].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length && axisConfig.y.length >= 2) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.LineStackedColumn].configOptions = (chartModel) => {
    const config = {
        highcharts: {
            plotOptions: {
                column: {
                    allowPointSelect: true,
                    crisp: false,
                    stacking: !!chartModel && isYAxisStackedAsPercent(chartModel)
                        ? 'percent'
                        : 'normal',
                    groupPadding: 0.01,
                },
                series: {
                    allowPointSelect: true,
                },
            },
            yAxis: [
                {
                    stackLabels: {
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.COLUMN_CHART, getEnvFlags().applyContrastForDataLabels),
                        useHTML: false,
                    },
                },
            ],
        },
        allowLegendWithMultipleY: true,
        displayYValuesWithLegendSeries: true,
        shouldBringSelectedSeriesToFront: false,
        hasStackedPointsForMultiSeries: true,
        minPointLengthSupported: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    responsiveLayout: true,
                    zoomPanStateToggle: true,
                    handleMissingValues: true,
                    showNullAsZero: true,
                },
            },
            excludeNullValues: true,
            showYAxisAsPercent: true,
            zoomPanStateToggle: true,
            yAxisRange: true,
            showDataLabels: true,
            showStackLabels: true,
            showDetailedLabels: true,
            showRoundedNumbers: true,
        },
    };
    if (chartModel && isYAxisStackedAsPercent(chartModel)) {
        const valueColumn = getYAxisColumns(chartModel)[0];
        config.highcharts.yAxis[0].labels = {
            formatter() {
                if (this.isTooltip) {
                    return getFormattedValueForNumericColumn(this.value, valueColumn);
                }
                return formatDataValue(this.value / 100, {
                    nDecimal: getStackLabelDecimalPrecision(chartModel),
                    isPercent: true,
                });
            },
        };
    }
    return config;
};
specImplementation[ChartType.LineStackedColumn].seriesOverride =
    seriesOverrides[ChartType.LineStackedColumn];
specImplementation[ChartType.LineStackedColumn].getEffectiveYAxisColumn = (chartModel, pointData, getTranslatedText) => {
    const yAxisColumns = getYAxisColumns(chartModel);
    let yAxisColumn;
    if (pointData.series.options.type ===
        getTranslatedText('charts.seriesType.LINE')) {
        yAxisColumn = getYAxisColumn(chartModel, pointData.series.name);
    }
    else {
        yAxisColumn = yAxisColumns[0];
    }
    return yAxisColumn;
};
specImplementation[ChartType.LineStackedColumn].highchartsType =
    ChartType.Column;
specImplementation[ChartType.LineStackedColumn].getStackedValuesColumn = getDefaultStackingValueColumn;
specImplementation[ChartType.LineStackedColumn].shouldShowStackLabelToggleForColumn = (chartModel, columnId) => {
    return isFirstYAxisColumn(chartModel, columnId);
};
specImplementation[ChartType.LineStackedColumn].shouldShowDetailLabelToggleForColumn = (chartModel, columnId) => {
    return isFirstYAxisColumn(chartModel, columnId);
};
specImplementation[ChartType.LineStackedColumn].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.LineStackedColumn].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
function basicShapeGeoTypeValidation(axisConfig) {
    if (!axisConfig.x || !axisConfig.y || !axisConfig.color) {
        return false;
    }
    if (axisConfig.x.length > 2 || axisConfig.y.length !== 1) {
        return false;
    }
    if (axisConfig.y[0] && !isMeasure(axisConfig.y[0])) {
        return false;
    }
    if (!isGeoColumn(axisConfig.x[0]) &&
        !isDateFamilyColumn(axisConfig.x[0]) &&
        isStandardRegionEnabled()) {
        return true;
    }
    const allXAxisColumnsValid = _.every(axisConfig.x, function (column) {
        return isGeoColumn(column);
    });
    if (!allXAxisColumnsValid) {
        return false;
    }
    return true;
}
function isAreaSupportedGeoColumn(geoColumn) {
    const geoConfig = getGeoConfig(geoColumn);
    if (geoConfig.getType() === GeoConfigType.CUSTOM_REGION) {
        return (!geoConfig.getGeometryType() ||
            AREA_CHART_ENABLED_GEOMETRIES.includes(geoConfig.getGeometryType()));
    }
    return (geoConfig.getType() === GeoConfigType.ADMIN_DIV_0 ||
        geoConfig.getType() === GeoConfigType.ADMIN_DIV_1 ||
        geoConfig.getType() === GeoConfigType.ADMIN_DIV_2);
}
function getAreaChartAxisConfigValidator() {
    return function (axisConfig) {
        if (!basicShapeGeoTypeValidation(axisConfig)) {
            return { isValid: false };
        }
        if (axisConfig.x.length !== 1 || axisConfig.y.length === 0) {
            return { isValid: false };
        }
        if (!isGeoColumn(axisConfig.x[0]) &&
            !isDateFamilyColumn(axisConfig.x[0]) &&
            isStandardRegionEnabled()) {
            return { isValid: true };
        }
        const allXAxisColumnsValid = _.every(axisConfig.x, function (column) {
            return isAreaSupportedGeoColumn(column);
        });
        return { isValid: !!allXAxisColumnsValid };
    };
}
specImplementation[ChartType.GeoArea] = {};
specImplementation[ChartType.GeoArea].configOptions = () => {
    return {
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                },
            },
            allowAxisCustomization: false,
            allowColorChange: true,
            gridLines: false,
            showRoundedNumbers: true,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: isAllowGradientColoring(),
            gradientEnabled: isAllowGradientColoring(),
            allowChartCustomisation: false,
            allowSortFormatOption: false,
        },
    };
};
specImplementation[ChartType.GeoArea].validateAxisConfig = getAreaChartAxisConfigValidator();
specImplementation[ChartType.GeoArea].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
function validateLatLongPairColumns(columns) {
    const latColumns = [];
    const longColumns = [];
    columns.forEach(function (column) {
        const geoConfig = getGeoConfig(column);
        if (!geoConfig) {
            return;
        }
        if (geoConfig.getType() === GeoConfigType.LATITUDE) {
            latColumns.push(column);
        }
        else if (geoConfig.getType() === GeoConfigType.LONGITUDE) {
            longColumns.push(column);
        }
    });
    if (latColumns.length > 0 || longColumns.length > 0) {
        if (latColumns.length !== 1 || longColumns.length !== 1) {
            return false;
        }
    }
    return true;
}
function basicPointGeoTypeValidation(axisConfig) {
    if (!basicShapeGeoTypeValidation(axisConfig)) {
        return { isValid: false };
    }
    const xAxisColumns = axisConfig.x;
    const yAxisColumns = axisConfig.y;
    if (!validateLatLongPairColumns(xAxisColumns)) {
        return { isValid: false };
    }
    if (yAxisColumns.length === 0) {
        return { isValid: false };
    }
    return { isValid: true };
}
specImplementation[ChartType.GeoBubble] = {};
specImplementation[ChartType.GeoBubble].validateAxisConfig = function (axisConfig) {
    return basicPointGeoTypeValidation(axisConfig);
};
specImplementation[ChartType.GeoBubble].configOptions = () => {
    return {
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                },
            },
            allowAxisCustomization: false,
            gridLines: false,
            showDataLabels: true,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: false,
            strokeWidth: 2,
            allowChartCustomisation: false,
            allowSortFormatOption: false,
        },
    };
};
specImplementation[ChartType.GeoBubble].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
specImplementation[ChartType.GeoHeatmap] = {};
specImplementation[ChartType.GeoHeatmap].validateAxisConfig = function (axisConfig) {
    var _a;
    if (!basicPointGeoTypeValidation(axisConfig).isValid) {
        return { isValid: false };
    }
    if (axisConfig.y.length !== 1) {
        return { isValid: false };
    }
    if (((_a = axisConfig.color) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        return { isValid: false };
    }
    return { isValid: true };
};
specImplementation[ChartType.GeoHeatmap].configOptions = () => {
    return {
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                    showAllLabels: false,
                },
            },
            allowAxisCustomization: false,
            showDataLabels: false,
            gridLines: false,
            allowColorChange: false,
            renameAxis: false,
            repositionAxis: false,
            conditionalFormatting: isAllowGradientColoring(),
            gradientEnabled: isAllowGradientColoring(),
            allowChartCustomisation: false,
            allowSortFormatOption: false,
        },
    };
};
specImplementation[ChartType.GeoEarthArea] = {};
specImplementation[ChartType.GeoEarthArea].validateAxisConfig = getAreaChartAxisConfigValidator();
specImplementation[ChartType.GeoEarthBubble] = specImplementation[ChartType.GeoEarthBar] = specImplementation[ChartType.GeoBubble];
specImplementation[ChartType.GeoEarthHeatmap] =
    specImplementation[ChartType.GeoHeatmap];
specImplementation[ChartType.GeoEarthGraph] = {};
specImplementation[ChartType.GeoEarthGraph].validateAxisConfig = function (axisConfig) {
    if (!basicPointGeoTypeValidation(axisConfig).isValid) {
        return { isValid: false };
    }
    if (!validateLatLongPairColumns(axisConfig.x)) {
        return { isValid: false };
    }
    if (!validateLatLongPairColumns(axisConfig.y)) {
        return { isValid: false };
    }
    const allYAxisColumnsGeo = _.every(axisConfig.y, function (column) {
        return { isValid: isGeoColumn(column) };
    });
    if (!allYAxisColumnsGeo) {
        return { isValid: false };
    }
    if (!axisConfig.size || !isEffectivelyNumeric(axisConfig.size)) {
        return { isValid: false };
    }
    return { isValid: true };
};
specImplementation[ChartType.GeoEarthGraph].configOptions = () => {
    return {
        radialEnabled: true,
        doNotDisplayLegend: true,
        attributesOnYAxis: true,
        noMeasuresOnYAxis: true,
        axesAlias: {
            xAxis: 'Origin',
            yAxis: 'Destination',
            radial: 'Value',
        },
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                },
            },
            allowAxisCustomization: false,
            geoProjectionType: true,
            gridLines: false,
            showYAxisAsPercent: false,
            zoomPanStateToggle: false,
            renameAxis: false,
            repositionAxis: false,
        },
    };
};
specImplementation[ChartType.GeoEarthGraph].shouldShowDataLabelToggleForColumn = shouldShowDataLabelToggleForYAxisColumns;
function isColumnStdDevOrVariance(column) {
    const aggType = getTrueEffectiveAggregateType(column);
    return (aggType === ColumnAggregationType.Variance ||
        aggType === ColumnAggregationType.StdDeviation);
}
specImplementation[ChartType.PivotTable] = {};
specImplementation[ChartType.PivotTable].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x || !axisConfig.y || !axisConfig.color) {
        return { isValid: false };
    }
    const containsMeasureNamesColumn = containsMeasureNamesOrMeasureValuesColumns(axisConfig);
    if (enableAttributesInPivotDataArea() && !containsMeasureNamesColumn) {
        if (!axisConfig.y.length) {
            return { isValid: false };
        }
    }
    else if (!areAllEffectivelyNumeric(axisConfig.y)) {
        if (containsMeasureNamesColumn) {
            return {
                isValid: false,
                errors: {
                    y: translate('chart.chartEditor.chartAxisConfigurator.MEASURE_COL_CONFIG_INVALID_FOR_PIVOT'),
                },
            };
        }
        return { isValid: false };
    }
    if (!areAllEffectivelyNonNumeric(axisConfig.x)) {
        return { isValid: false };
    }
    if (!areAllAttributes(axisConfig.color)) {
        return { isValid: false };
    }
    const isStdDevOrVariance = axisConfig.y.some(function (column) {
        return isColumnStdDevOrVariance(column);
    });
    if (isStdDevOrVariance) {
        return { isValid: false };
    }
    if (axisConfig.y.length &&
        axisConfig.x.length + axisConfig.color.length &&
        !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
export const isPivotDataSetComplete = (chartModel, dataModel) => {
    const batchSize = parseInt(configOptions(getChartType(chartModel), chartModel).batchSize, 10);
    const isDataWithinBatchSizeLimits = getTotalRowCount(chartModel) <= batchSize;
    if (isDataWithinBatchSizeLimits &&
        getSamplingRatio(chartModel) === 1 &&
        dataModel.isDataSourceDefault) {
        return true;
    }
    return getTotalRowCount(chartModel) < batchSize;
};
export const getPivotSummariesStateWithAdditionalChecks = (chartModel, axisConfig) => {
    const enforceToShowSummaries = (axisConfig.x.length > 0 && axisConfig.color.length === 0) ||
        (axisConfig.x.length === 0 && axisConfig.color.length > 0);
    const isTreeLayoutEnabled = getUseTreeLayout(chartModel);
    const pivotSummariesState = getPivotSummariesState(chartModel);
    const chartConfigOptions = configOptions(getChartType(chartModel), null);
    const showGrandTotals = (_.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals) &&
        _.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals)
        ? chartConfigOptions.isSummaryEnabledByDefault
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals) &&
            !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals)) ||
        enforceToShowSummaries;
    const showRowGrandTotals = (_.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals)
        ? chartConfigOptions.isSummaryEnabledByDefault
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals)) ||
        enforceToShowSummaries;
    const showColumnGrandTotals = (_.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals)
        ? chartConfigOptions.isSummaryEnabledByDefault
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals)) ||
        enforceToShowSummaries;
    const showRowTotals = (isTreeLayoutEnabled || _.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowTotals)
        ? chartConfigOptions.isSummaryEnabledByDefault
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowTotals)) || enforceToShowSummaries;
    const showColumnTotals = (_.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnTotals)
        ? chartConfigOptions.isSummaryEnabledByDefault
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnTotals)) ||
        enforceToShowSummaries;
    return {
        showGrandTotals,
        showRowGrandTotals,
        showColumnGrandTotals,
        showRowTotals,
        showColumnTotals,
    };
};
specImplementation[ChartType.PivotTable].getChartDataValidationError = (chartModel, dataModel) => {
    if (!isPivotDataSetComplete(chartModel, dataModel)) {
        return { type: ErrorTypes.DATA_INCOMPLETE_PIVOT };
    }
    return null;
};
specImplementation[ChartType.PivotTable].configOptions = function () {
    return {
        highcharts: {
            ...blinkConstants.commonChartConfig,
        },
        radialEnabled: false,
        doNotShowLegend: true,
        attributesOnYAxis: enableAttributesInPivotDataArea(),
        axesAlias: {
            xAxis: translate('chart.chartEditor.chartAxisConfigurator.ROWS_AXIS'),
            yAxis: enableAttributesInPivotDataArea()
                ? translate('chart.chartEditor.chartAxisConfigurator.ROWS_VALUES')
                : translate('chart.chartEditor.chartAxisConfigurator.ROWS_MEAURES'),
            legend: 'Columns',
        },
        batchSize: 100000,
        allowLegendWithMultipleY: true,
        isSummaryEnabledByDefault: true,
        allowedConfigurations: {
            conditionalFormatEditorParams: {
                isPlotAsBand: false,
                isFont: true,
                isBackgroundColorPickerAvailable: true,
                isConditionalFormattingAllowedOnAttributeColumn: true,
            },
            allowAxisCustomization: false,
            advanced: {
                display: {
                    overlayHeatMap: true,
                    summaries: false,
                    pivotSummaries: true,
                    pivotSummariesAlignment: true,
                    summaryMode: true,
                    treeLayout: true,
                    showDataFieldArea: true,
                    gridLines: false,
                    showAllLabels: false,
                    showMaxDataPoints: false,
                    tooltipCustomization: false,
                },
            },
            showDataLabels: false,
            gridLines: false,
            renameAxis: false,
            repositionAxis: false,
            yAxisRange: false,
            conditionalFormatting: true,
            allowEnhancedConditionalFormatting: false,
            allowColorChange: false,
            optionalXAxisColumns: true,
            isSummaryModeAllowed: true,
            allowAttributesInPivotData: enableAttributesInPivotDataArea(),
            disableSortForAttributeColumns: true,
            allowChartCustomisation: false,
            allowSortFormatOption: false,
            measureNameValueColumns: {
                enableMeasureNameColumn: true,
                measureNameColumnAlias: translate('pivotTable.MEASURE_NAMES_ALIAS'),
                measureValueColumnAlias: translate('pivotTable.MEASURE_VALUES_ALIAS'),
            },
        },
    };
};
specImplementation[ChartType.PivotTable].getQueryDefinitions = function (axisConfig, chartModel) {
    if (!chartModel) {
        logger.log('ChartModel is Null or Undefined');
        return [];
    }
    const measureDataColumns = axisConfig.y.filter(yAxisColumn => isEffectivelyNumeric(yAxisColumn));
    const attributeDataColumns = axisConfig.y.filter(yAxisColumn => isAttribute(yAxisColumn));
    const queryDefs = [];
    queryDefs.push({
        x: axisConfig.x,
        y: axisConfig.y,
        color: axisConfig.color,
        sort: axisConfig.sort,
    });
    if (attributeDataColumns.length > 0 && measureDataColumns.length === 0) {
        return queryDefs;
    }
    if (attributeDataColumns.length) {
        queryDefs.push({
            x: axisConfig.x,
            y: measureDataColumns,
            color: axisConfig.color,
            sort: axisConfig.sort,
        });
    }
    const pivotSummariesState = getPivotSummariesStateWithAdditionalChecks(chartModel, axisConfig);
    if (pivotSummariesState.showGrandTotals) {
        queryDefs.push({
            x: [],
            y: measureDataColumns,
            color: [],
            sort: axisConfig.sort,
        });
    }
    const pivotState = getPivotState(chartModel);
    const rowExpandedPaths = (pivotState === null || pivotState === void 0 ? void 0 : pivotState.rowExpandedPaths) || [];
    const columnExpandedPaths = (pivotState === null || pivotState === void 0 ? void 0 : pivotState.columnExpandedPaths) || [];
    const maxRowExpandedLength = Math.max(...[
        0,
        ...rowExpandedPaths.map(expandedPathArray => expandedPathArray.paths.length),
    ]);
    const maxColExpandedLength = Math.max(...[
        0,
        ...columnExpandedPaths.map(expandedPathArray => expandedPathArray.paths.length),
    ]);
    const rowExpandedPathLengthByField = getMaxExpandedPathLength('row', maxRowExpandedLength, pivotState, axisConfig.x);
    const colExpandedLengthByField = getMaxExpandedPathLength('column', maxColExpandedLength, pivotState, axisConfig.color);
    const requiredXAxisColumns = axisConfig.x.slice(0, rowExpandedPathLengthByField + 1);
    if (pivotSummariesState.showColumnGrandTotals) {
        requiredXAxisColumns.forEach((xColumn, xIdx) => {
            const rowColumns = axisConfig.x.slice(0, xIdx + 1);
            queryDefs.push({
                x: rowColumns,
                y: measureDataColumns,
                color: [],
                sort: axisConfig.sort,
            });
        });
    }
    const requiredLegendColumns = axisConfig.color.slice(0, colExpandedLengthByField + 1);
    if (pivotSummariesState.showRowGrandTotals) {
        requiredLegendColumns.forEach((colorColumn, colorIdx) => {
            const colColumns = axisConfig.color.slice(0, colorIdx + 1);
            queryDefs.push({
                x: [],
                y: measureDataColumns,
                color: colColumns,
                sort: axisConfig.sort,
            });
        });
    }
    requiredXAxisColumns.forEach((xColumn, xIdx) => {
        const rowColumns = axisConfig.x.slice(0, xIdx + 1);
        requiredLegendColumns.forEach((colorColumn, colorIdx) => {
            const colColumns = axisConfig.color.slice(0, colorIdx + 1);
            if (!(rowColumns.length === axisConfig.x.length &&
                colColumns.length === axisConfig.color.length)) {
                queryDefs.push({
                    x: rowColumns,
                    y: measureDataColumns,
                    color: colColumns,
                    sort: axisConfig.sort,
                });
            }
        });
    });
    return queryDefs;
};
specImplementation[ChartType.PivotTable].translateAxisConfigWithMeasureNamesValues = (chartModel) => {
    var _a, _b;
    const axisConfigs = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _a === void 0 ? void 0 : _a.axisConfig;
    if (((_b = axisConfigs[0].measureValues) === null || _b === void 0 ? void 0 : _b.length) > 0) {
        const translatedAxisConfigs = axisConfigs.map(axisConfig => {
            const updatedAxisConfig = _.cloneDeep(axisConfig);
            if (axisConfig.measureValues &&
                axisConfig.measureValues.length > 0) {
                updatedAxisConfig.y = axisConfig.measureValues;
                updatedAxisConfig.measureValues = [];
            }
            return updatedAxisConfig;
        });
        return translatedAxisConfigs;
    }
    return axisConfigs;
};
specImplementation[ChartType.PivotTable].translateQueryDefinitionWithMeasureNamesValues = (axisConfigs, chartModel) => {
    if (!containsMeasureNamesOrMeasureValuesColumns(axisConfigs[0])) {
        return axisConfigs;
    }
    const measureValueColumn = chartModel.columns.find(column => {
        return (column.column.chartSpecificColumnType ===
            ChartSpecificColumnType.MeasureValues);
    });
    if (_.isNil(measureValueColumn)) {
        logger.error('measureValueColumn not found in the chartModel', {
            axisConfigs,
            chartModel,
        });
        return axisConfigs;
    }
    return axisConfigs.map(axisConfig => {
        const outputAxisConfig = _.cloneDeep(axisConfig);
        outputAxisConfig.measureValues = axisConfig.y;
        outputAxisConfig.y = [measureValueColumn.column];
        return outputAxisConfig;
    });
};
specImplementation[ChartType.Candlestick] = {};
specImplementation[ChartType.Candlestick].highchartsType =
    ChartType.Candlestick;
specImplementation[ChartType.Candlestick].validateAxisConfig = function (axisConfig) {
    var _a;
    if (!axisConfig.x || !axisConfig.y) {
        return { isValid: false };
    }
    if (!areAllEffectivelyNumeric(axisConfig.y)) {
        return { isValid: false };
    }
    if (axisConfig.x.length &&
        axisConfig.y.length === 4 &&
        !((_a = axisConfig.color) === null || _a === void 0 ? void 0 : _a.length) &&
        !axisConfig.size) {
        return { isValid: true };
    }
    return { isValid: false };
};
specImplementation[ChartType.Candlestick].getYAxisTooltipData = (chartModel, dataModel, pointData, getTranslatedText) => {
    const yAxisTooltipData = [];
    const yAxisColumns = dataModel.getQueryDefinitions()[0].yAxisColumns;
    CANDLE_STICK_CHART_DATA_KEYS.forEach((key, idx) => {
        const yAxisColumn = yAxisColumns[idx];
        yAxisTooltipData.push({
            label: getName(yAxisColumn),
            value: getDataLabelFromColumn(chartModel, dataModel, yAxisColumn, pointData.point[key], getTranslatedText),
        });
    });
    return yAxisTooltipData;
};
specImplementation[ChartType.Candlestick].configOptions = () => ({
    axesAlias: {
        yAxis: 'chart.chartEditor.chartAxisConfigurator.Y_AXIS_CANDLESTICK',
    },
    enableLegendSelector: false,
    doNotShowLegend: true,
    allowedConfigurations: {
        advanced: {
            display: {
                zoomPanStateToggle: true,
                useCustomColorSelector: true,
                responsiveLayout: true,
                showAllLabels: false,
            },
        },
        supportColumnLevelColorChange: false,
        useCustomColorSelector: true,
        showDataLabels: false,
        zoomPanStateToggle: true,
        yAxisRange: true,
        regressionLine: false,
        showRoundedNumbers: true,
        shouldOnlyContainOneYAxis: true,
        showYAxisLinkingOption: false,
        allowSortFormatOption: false,
    },
});
specImplementation[ChartType.Candlestick].customColorSelectorConfig = (chartModel, onColorSelect, getTranslatedText) => {
    let customColors = !!chartModel && chartModel.vizProp.customColorSelectorArray;
    if (_.isEmpty(customColors) || customColors.length === 0) {
        customColors = chartSpecificConstants.DEFAULT_CANDLESTICK_COLORS;
    }
    const labelForUpMark = getTranslatedText('chart.chartEditor.customColorConfigurator.candlestick.UP_COLOR');
    const labelForDownMark = getTranslatedText('chart.chartEditor.customColorConfigurator.candlestick.DOWN_COLOR');
    const colorSelectUpMarkOption = {
        label: labelForUpMark,
        color: customColors[0],
        closeCallback: color => {
            let customColorsArray = _.cloneDeep(!!chartModel && chartModel.vizProp.customColorSelectorArray);
            if (_.isEmpty(customColorsArray) ||
                customColorsArray.length === 0) {
                customColorsArray =
                    chartSpecificConstants.DEFAULT_CANDLESTICK_COLORS;
            }
            customColorsArray[0] = color;
            onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], _.cloneDeep(customColorsArray));
        },
    };
    const colorSelectDownMarkOption = {
        label: labelForDownMark,
        color: customColors[1],
        closeCallback: color => {
            let customColorsArray = _.cloneDeep(!!chartModel && chartModel.vizProp.customColorSelectorArray);
            if (_.isEmpty(customColorsArray) ||
                customColorsArray.length === 0) {
                customColorsArray =
                    chartSpecificConstants.DEFAULT_CANDLESTICK_COLORS;
            }
            customColorsArray[1] = color;
            onColorSelect(chartModel.vizProp, ['customColorSelectorArray'], _.cloneDeep(customColorsArray));
        },
    };
    return [colorSelectUpMarkOption, colorSelectDownMarkOption];
};
specImplementation[ChartType.Candlestick].seriesOverride =
    seriesOverrides[ChartType.Candlestick];
specImplementation[ChartType.Candlestick].getGridLineConfig =
    specImplementation[ChartType.Column].getGridLineConfig;
specImplementation[ChartType.GridTable] = {};
specImplementation[ChartType.GridTable].validateAxisConfig = function (axisConfig) {
    if (!axisConfig.x) {
        return { isValid: false };
    }
    return { isValid: true };
};
specImplementation[ChartType.GridTable].configOptions = () => {
    return {
        radialEnabled: false,
        enableLegendSelector: false,
        doNotShowLegend: true,
        axesAlias: {
            xAxis: 'Columns',
            yAxis: 'Groups',
        },
        batchSize: 100000,
        allowLegendWithMultipleY: false,
        attributesOnYAxis: true,
        noMeasuresOnYAxis: true,
        allowedConfigurations: {
            advanced: {
                display: {
                    gridLines: false,
                },
            },
            showDataLabels: false,
            gridLines: false,
            renameAxis: false,
            repositionAxis: false,
        },
    };
};
function doesSupportHighCardinalityData(chartType) {
    return !!highCardinalityDataPointSupportedCharts[chartType];
}
function isStackedChart(chartType) {
    return stackedChartTypes.includes(chartType);
}
function isLineStackedColumnChart(chartType) {
    return chartType === ChartType.LineStackedColumn;
}
function getStackedValuesColumn(chartType, queryDefinitions) {
    if (specImplementation[chartType].getStackedValuesColumn) {
        return specImplementation[chartType].getStackedValuesColumn(queryDefinitions);
    }
    return undefined;
}
function getYSEOPConfig(chartModel) {
    const chartType = getChartType(chartModel);
    const chartTypeSpecImplementation = specImplementation[chartType];
    if (!chartTypeSpecImplementation) {
        logger.error('Chart specification implementation missing for chart type:', chartType);
        return;
    }
    if (!chartTypeSpecImplementation.getYSEOPConfig) {
        logger.warn('Chart doesnt Support yseop:', chartType);
        return {};
    }
    return chartTypeSpecImplementation.getYSEOPConfig(chartModel);
}
function areProvidersEqual(chartAType, chartBtype) {
    return chartTypeByProvider[chartAType] === chartTypeByProvider[chartBtype];
}
export function isMultiQueryChart(chartType) {
    return getMultiQueryChartTypes().includes(chartType);
}
function getQueryDefinitions(chartType, axisConfig, chartModel) {
    if (isMultiQueryChart(chartType)) {
        return specImplementation[chartType].getQueryDefinitions(axisConfig, chartModel);
    }
    return [axisConfig];
}
function getEffectiveBarWidthForNonResponsiveLayout(chartType, series, refSize) {
    let effectiveBarWidth = refSize * 2;
    if (!configOptions(chartType, null).hasStackedPointsForMultiSeries) {
        const numBars = series.length;
        effectiveBarWidth = numBars * effectiveBarWidth;
    }
    return effectiveBarWidth;
}
function getUniqueIdForVizCol(column) {
    return getId(column);
}
function getDOMStringForChartElement(chartProvider, element) {
    if (chartProvider === chartProviders.HIGHCHART ||
        chartProvider === chartProviders.NETWORK) {
        return element.innerHTML;
    }
    return '';
}
const getDefaultCustomPropsForChart = (chartType) => {
    if (chartType === ChartType.Kpi) {
        return {
            showLabel: true,
            showComparison: true,
            showSparkline: true,
            showAnomalies: true,
            showAnalyseChangeCta: true,
            showBounds: true,
            customCompare: TupleType.PrevAvailable,
        };
    }
    if (isGeoChartType(chartType)) {
        return {
            tileType: GEO_TILE_TYPES.MAPBOX.LIGHT,
        };
    }
    return undefined;
};
const getXAxisLabelMaxWidth = (chartHeight, isXAxisVertical) => {
    if (!isXAxisVertical) {
        return chartHeight * 0.3;
    }
    return undefined;
};
export { HIDDEN_CHARTS, chartProviders, stackedVersionForChartType, chartTypeInDisplayOrderWithKpiEnabled, chartTypeInDisplayOrder, muzePhase1Charts, muzePhase2Charts, chartTypeSwaps, tsHostedCustomCharts, getLegendLabels, hasPersistentLegend, areProvidersEqual, hasNativeLegend, getQueryDefinitions, getUniqueIdForVizCol, doesSupportHighCardinalityData, isGeoChartType, isStackedChart, isLineStackedColumnChart, isGeoMapChartType, isGeoEarthChartType, isPivotChartType, isHighchartType, isNetworkChartType, getChartProvider, validateAxisConfig, seriesOverride, customColorSelectorConfig, configOptions, getChartTooltip, isXAxisVertical, getHighchartsType, getDefaultBatchSize, useMeasureOnYAxisAsOrdinal, useOrdinalXAxis, getStackedValuesColumn, getYSEOPConfig, getEffectiveBarWidthForNonResponsiveLayout, getDOMStringForChartElement, areAllEffectivelyNumeric, areAllAttributes, shouldShowDataLabelToggleForColumn, shouldShowStackLabelToggleForColumn, shouldShowDetailLabelToggleForColumn, getChartSpecificDataValidationError, getChartSpecificCardinalityError, getChartBasedGridLineConfig, hasAxisLabels, translateAxisConfigWithMeasureNamesValues, translateQueryDefinitionWithMeasureNamesValues, getCFAllowedDimensionForTSHostedCharts, getDimensionHandlingMappingForTSHostedCharts, getCustomAxisConfigToCustomChartConfigTransformerForTSHostedCharts, getDefaultCustomPropsForChart, getXAxisLabelMaxWidth, };
//# sourceMappingURL=chart-type-specification-service.js.map